import ExportButton, { ExportIconButton } from '../../components/ExportButton';
import { Steps } from "antd";
import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { geocodeByAddress, getLatLng } from "react-google-places-autocomplete";
import { FiEye, FiPlus } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { PiMoney, PiPencilSimpleLine } from "react-icons/pi";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import CustomModal from "../../components/CustomModal";
import CustomTabs from "../../components/CustomTabs";
import FileUpload from "../../components/FileUpload";
import GoogleMapInput from "../../components/GoogleMapInput";
import Input from "../../components/input";
import ConfirmDeleteModal from "../../components/modals/ConfirmDeleteModal";
import NewTableComponent from "../../components/NewTableComponent";
import PhoneNumberInput from "../../components/PhoneNumberInput";
import Select from "../../components/Select";
import StickyActionsTable from "../../components/StickyActionsTable";
import UserAvatar from "../../components/UserAvatar";
import {
  createCustomer,
  fetchCustomerDetail,
  updateCustomer,
} from "../../store/Slices/CustomerSlice";
import {
  fetchBanksDropdown,
  fetchCostCenterDropdown,
  fetchPartsUnitsDropdown,
  fetchTransporterCitiesDropdown,
  fetchUsersDropdown,
} from "../../store/Slices/dropdownSlice";
import "./Customer.css";
import "../../styles/Services.css";

import moment from "moment";
import { apiService } from "../../services/apiService";
import {
  AppMainModules,
  CustomersPermission,
  CustomersSubModules,
} from "../../utils/app-constant";
import {
  formatLocalDateTime,
  formatOnlyDate,
  formats,
  hasPermission,
  modules,
} from "../../utils/helper";
import {
  detectPakistanProvince as detectProvince,
  getProvinceOption,
  normalizeProvinceValue,
  provinceOptions,
} from "../../utils/provinceOptions";
import { errorNotify, successNotify } from "../../utils/toast";
import { buildPhoneNumberSchema } from "./shared/phoneValidation";

import {
  STATUS_ID_TO_KEY,
  billingFrequencyOptions,
  cityOptions,
  designationOptions,
  fallbackBankOptions,
  goodsTypeOptions,
  industryTypeOptions,
  insuranceCoverTypeOptions,
  insuranceStatusOptions,
  locationStatusOptions,
  paymentMethodOptions,
  paymentTermOptions,
  productTypeOptions,
  resolveWarehouseStatus,
  statusOptions,
  stepItems,
} from "./customerEnums";

const toText = (value) => String(value ?? "").trim();
const resolveCoordinateText = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};
const resolveStatus = (status) => {
  const statusNumber = Number(status);
  if (Number.isFinite(statusNumber) && STATUS_ID_TO_KEY[statusNumber]) {
    return STATUS_ID_TO_KEY[statusNumber];
  }
  return normalize(status);
};

const resolveGoodsValue = (goods) =>
  toText(goods?.title ?? goods?.name ?? goods?.label ?? goods?.slug ?? goods);

const defaultNotes = "";

let tempIdCounter = 1;
const createTempId = () => Date.now() + tempIdCounter++;

const createSecondary = () => ({
  id: createTempId(),
  name: "",
  contact: "",
  designation: "",
  email: "",
});

const createWarehouse = () => ({
  id: createTempId(),
  warehouseName: "",
  city: "",
  province: "",
  contact: "",
  contactPersonName: "",
  contactPersonNumber: "",
  warehouseAddress: "",
  warehouseLatitude: "",
  warehouseLongitude: "",
  typesOfGoods: [],
  status: "",
});

const createCustomerLocationDraft = () => createWarehouse();

const createProduct = () => ({
  id: createTempId(),
  name: "",
  unit: "",
  productType: "",
  productValue: "",
  insuranceCoverType: "",
  insuranceStatus: "",
  insuranceCoverValue: "",
});

const minimumWarehouseCount = 2;
const minimumProductCount = 1;
const fallbackCustomerIdPrefix = "TPC-";

const ensureMinimumWarehouses = (warehouses = []) => {
  const nextWarehouses = Array.isArray(warehouses) ? warehouses : [];
  if (nextWarehouses.length >= minimumWarehouseCount) return nextWarehouses;

  return [
    ...nextWarehouses,
    ...Array.from(
      { length: minimumWarehouseCount - nextWarehouses.length },
      () => createWarehouse()
    ),
  ];
};

const ensureMinimumProducts = (products = []) => {
  const nextProducts = Array.isArray(products) ? products : [];
  if (nextProducts.length >= minimumProductCount) return nextProducts;

  return [
    ...nextProducts,
    ...Array.from(
      { length: minimumProductCount - nextProducts.length },
      () => createProduct()
    ),
  ];
};

const isSavedProduct = (product) => {
  return (
    product &&
    product.id &&
    !String(product.id).startsWith("product-") &&
    !isNaN(Number(product.id))
  );
};

const defaultValues = () => ({
  customerId: "",
  customerName: "",
  contact: "",
  email: "",
  headquarter: "",
  address: "",
  addressLatitude: "",
  addressLongitude: "",
  ntn_number: "",
  sntn_number: "",
  industryType: "",
  typesOfGoods: [],
  status: "",
  costCenterId: "",
  primaryContact: { name: "", contact: "", designation: "", email: "" },
  secondaryContacts: [],
  noWarehouses: false,
  warehouses: ensureMinimumWarehouses(),
  products: ensureMinimumProducts(),
  attachments: [],
  payment: {
    billingFrequency: "",
    paymentMethod: "",
    paymentTerm: "",
    bank: "",
    accountTitle: "",
    iban: "",
  },
  notes: defaultNotes,
});

const createCustomerRouteForm = () => ({
  fromWarehouseId: "",
  toWarehouseId: "",
  locationRate: "",
  fixDistance: "",
  changedAt: "",
});

const clone = (value) => JSON.parse(JSON.stringify(value));
const safeArray = (value) => (Array.isArray(value) ? value : []);
const normalize = (value) => String(value || "").trim().toLowerCase();
const unique = (value) => [...new Set(safeArray(value).filter(Boolean))];
const cleanText = (value) => String(value || "").trim();
const firstFilled = (...values) => values.map(cleanText).find(Boolean) || "";
const hasFilledContact = (item) =>
  Boolean(
    cleanText(item?.name) ||
    cleanText(item?.email) ||
    cleanText(item?.contact) ||
    cleanText(item?.designation)
  );
const hasFilledWarehouse = (item) =>
  Boolean(
    cleanText(item?.warehouseName) ||
    cleanText(item?.city) ||
    cleanText(item?.contact) ||
    cleanText(item?.contactPersonName) ||
    cleanText(item?.contactPersonNumber) ||
    cleanText(item?.warehouseAddress) ||
    cleanText(item?.warehouseLatitude) ||
    cleanText(item?.warehouseLongitude) ||
    cleanText(item?.province)
  );
const hasFilledProduct = (item) =>
  Boolean(
    cleanText(item?.name) ||
    cleanText(item?.unit) ||
    cleanText(item?.productType) ||
    cleanText(item?.productValue) ||
    cleanText(item?.insuranceCoverType) ||
    cleanText(item?.insuranceStatus) ||
    cleanText(item?.insuranceCoverValue)
  );
const toSentenceCase = (value) => {
  const text = cleanText(value).replace(/_/g, " ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};
const stripHtml = (value) =>
  cleanText(
    String(value || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
  );

const resolveUserName = (user = {}) => {
  const profile = user?.profile || user?.user_profile || {};
  const profileFullName = firstFilled(
    profile?.name,
    profile?.full_name,
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ")
  );

  return firstFilled(
    profileFullName,
    user?.full_name,
    user?.name,
    user?.username,
    user?.email
  );
};

const resolveUserImage = (user = {}) => {
  const profile = user?.profile || user?.user_profile || {};

  return firstFilled(
    profile?.image,
    profile?.profile_image,
    profile?.avatar,
    user?.image,
    user?.profile_image,
    user?.avatar
  );
};

const toOptionalNumber = (value) => {
  const normalizedValue = String(value ?? "").trim();
  if (!normalizedValue) return "";
  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : normalizedValue;
};

const extractPositiveNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
};

const resolveCustomerRouteLocationOptions = (response) => {
  const payload = response?.data || response || {};

  return safeArray(payload?.data || payload?.results || payload)
    .map((item) => {
      const value = cleanText(item?.value ?? item?.id ?? item?.warehouse_id);
      const label = cleanText(
        item?.label ?? item?.name ?? item?.warehouse_name ?? item?.location
      );

      if (!value || !label) return null;

      return { value, label };
    })
    .filter(Boolean);
};

const resolveCustomerRouteList = (response) => {
  const payload = response?.data || response || {};

  return safeArray(
    payload?.results ||
    payload?.routes ||
    payload?.data?.results ||
    payload?.data?.routes ||
    payload
  );
};

const getCustomerRouteWarehouse = (route = {}, direction = "from") =>
  route?.[`${direction}_warehouse`] ||
  route?.[`${direction}Warehouse`] ||
  route?.[`${direction}_location`] ||
  route?.[`${direction}Location`] ||
  {};

const resolveCustomerRouteWarehouseLabel = (route = {}, lookup = {}, direction = "from") => {
  const warehouse = getCustomerRouteWarehouse(route, direction);

  const directLabel = firstFilled(
    warehouse?.warehouse_name,
    warehouse?.name,
    warehouse?.location,
    route?.[`${direction}WarehouseLabel`],
    route?.[`${direction}LocationLabel`],
    route?.[`${direction}_warehouse_name`],
    route?.[`${direction}_location_name`]
  );

  if (directLabel) return directLabel;

  const warehouseId = resolveCustomerRouteWarehouseId(route, direction);
  return lookup?.[warehouseId] || warehouseId || "--";
};

const resolveCustomerRouteWarehouseId = (route = {}, direction = "from") => {
  const warehouse = getCustomerRouteWarehouse(route, direction);

  return firstFilled(
    route?.[`${direction}_warehouse_id`],
    route?.[`${direction}WarehouseId`],
    route?.[`${direction}_location_id`],
    route?.[`${direction}LocationId`],
    route?.[`${direction}_customer_warehouse_id`],
    warehouse?.id,
    warehouse?.warehouse_id,
    warehouse?.customer_warehouse_id,
    warehouse?.value
  );
};

const withMissingRouteLocationOptions = (options = [], selectedRoutes = []) => {
  const nextOptions = safeArray(options).filter(Boolean);
  const existingValues = new Set(
    nextOptions.map((option) => normalize(option?.value)).filter(Boolean)
  );

  safeArray(selectedRoutes).forEach((route) => {
    ["from", "to"].forEach((direction) => {
      const value = resolveCustomerRouteWarehouseId(route, direction);
      const label = resolveCustomerRouteWarehouseLabel(route, {}, direction);

      if (!value || existingValues.has(normalize(value))) return;

      nextOptions.push({
        value,
        label: label && label !== "--" ? label : value,
      });
      existingValues.add(normalize(value));
    });
  });

  return nextOptions;
};

const resolveCustomerRouteRecord = (routes = [], routeId) => {
  const normalizedRouteId = cleanText(routeId);
  if (!normalizedRouteId) return null;

  return (
    safeArray(routes).find((route) => {
      const candidates = [
        route?.id,
        route?.customer_route_id,
        route?.route_id,
      ]
        .map(cleanText)
        .filter(Boolean);

      return candidates.includes(normalizedRouteId);
    }) || null
  );
};

const formatDateTimeValue = (value) => {
  const formattedValue = formatLocalDateTime(value);
  return formattedValue === "N/A" ? "--" : formattedValue;
};

const formatDateValue = (value) => {
  const formattedValue = formatOnlyDate(value);
  return formattedValue === "N/A" ? "--" : formattedValue;
};

const goodsTypeTextToValueMap = new Map(
  safeArray(goodsTypeOptions).flatMap((item) => {
    const labelKey = normalize(item?.label);
    const valueKey = normalize(item?.value);
    return [
      [labelKey, item?.value],
      [valueKey, item?.value],
    ];
  })
);

const resolveGoodsTypeId = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const directNumber = Number(value);
  if (Number.isFinite(directNumber) && directNumber > 0) return directNumber;
  const mapped = goodsTypeTextToValueMap.get(normalize(value));
  const mappedNumber = Number(mapped);
  return Number.isFinite(mappedNumber) && mappedNumber > 0 ? mappedNumber : null;
};

const resolveUploadedFilePath = (file) => {
  if (typeof file === "string") return cleanText(file);
  if (!file || typeof file !== "object") return "";
  return cleanText(
    file?.file_path ||
    file?.fileName ||
    file?.file_name ||
    file?.path ||
    file?.url ||
    file?.file ||
    file?.image ||
    file?.originalName ||
    ""
  );
};

const touchedFromErrors = (errors) => {
  if (Array.isArray(errors)) return errors.map((item) => touchedFromErrors(item));
  if (errors && typeof errors === "object") {
    return Object.keys(errors).reduce((acc, key) => {
      acc[key] = touchedFromErrors(errors[key]);
      return acc;
    }, {});
  }
  return true;
};

const flattenErrorPaths = (errors, parentPath = "") => {
  if (!errors) return [];
  if (typeof errors === "string") return parentPath ? [parentPath] : [];

  if (Array.isArray(errors)) {
    return errors.flatMap((item, index) =>
      flattenErrorPaths(item, `${parentPath}[${index}]`)
    );
  }

  if (typeof errors === "object") {
    return Object.keys(errors).flatMap((key) => {
      const nextParentPath = parentPath ? `${parentPath}.${key}` : key;
      return flattenErrorPaths(errors[key], nextParentPath);
    });
  }

  return [];
};

const scrollToFirstError = (errors) => {
  if (typeof document === "undefined") return;

  const errorPaths = flattenErrorPaths(errors);
  const fieldTargets = errorPaths
    .flatMap((errorPath) => {
      const wrapper = Array.from(document.querySelectorAll("[data-field-name]")).find(
        (element) => element.getAttribute("data-field-name") === errorPath
      );
      const field = document.querySelector(`[name="${errorPath.replace(/"/g, '\\"')}"]`);

      return [wrapper, field?.closest?.(".input_wrapper"), field].filter(Boolean);
    })
    .filter((element, index, list) => list.indexOf(element) === index);

  const visibleTargets = fieldTargets
    .filter((element) => element.getClientRects().length > 0)
    .sort(
      (a, b) =>
        a.getBoundingClientRect().top +
        window.scrollY -
        (b.getBoundingClientRect().top + window.scrollY)
    );

  const target =
    visibleTargets[0] ||
    document.querySelector(".error-message, .invalid-feedback, [aria-invalid='true']");

  if (!target) return;

  target.scrollIntoView({ behavior: "smooth", block: "center" });

  const focusTarget =
    target.querySelector?.("input:not([type='hidden']), textarea, button, [tabindex]") ||
    (typeof target.focus === "function" ? target : null);

  window.setTimeout(() => {
    focusTarget?.focus?.({ preventScroll: true });
  }, 250);
};

const extractIdMeta = (value) => {
  const text = cleanText(value);
  if (!text) return null;

  const match = text.match(/^(.*?)(\d+)$/);
  if (!match) return null;

  const number = Number(match[2]);
  if (!Number.isFinite(number)) return null;

  return {
    prefix: match[1],
    number,
    padding: match[2].length,
  };
};

const generateNextCustomerId = (rows = []) => {
  let highestCustomerId = null;
  let highestRecordId = 0;

  safeArray(rows).forEach((item) => {
    const customerIdMeta = extractIdMeta(item?.customer_id ?? item?.customerId);
    if (
      customerIdMeta &&
      (!highestCustomerId || customerIdMeta.number > highestCustomerId.number)
    ) {
      highestCustomerId = customerIdMeta;
    }

    const recordId = Number(item?.id ?? item?.recordId);
    if (Number.isFinite(recordId) && recordId > highestRecordId) {
      highestRecordId = recordId;
    }
  });

  if (highestCustomerId) {
    return `${highestCustomerId.prefix}${String(
      highestCustomerId.number + 1
    ).padStart(highestCustomerId.padding, "0")}`;
  }

  return `${fallbackCustomerIdPrefix}${String(
    Math.max(highestRecordId + 1, 1)
  ).padStart(4, "0")}`;
};

const resolveOptionText = (options, selectedValue) => {
  const selected = cleanText(selectedValue);
  if (!selected) return "";

  const matched = safeArray(options).find(
    (option) =>
      normalize(option?.value) === normalize(selected) ||
      normalize(option?.label) === normalize(selected)
  );

  return cleanText(matched?.label ?? selectedValue);
};

const toFormValues = (record) => {
  const base = defaultValues();
  if (!record) return base;

  const data = record?.formData ? clone(record.formData) : {};
  const warehouseSource = safeArray(data.warehouses).length
    ? safeArray(data.warehouses)
    : safeArray(record.warehouses);
  const productSource = safeArray(data.products).length
    ? safeArray(data.products)
    : safeArray(record.products || record.customer_products);

  return {
    ...base,
    ...data,
    customerId: record.id || data.customerId || "",
    customerName: data.customerName || record.name || "",
    contact: data.contact || record.contact || "",
    email: data.email || record.email || "",
    headquarter: data.headquarter || record.headquarter || "",
    address: data.address || record.address || "",
    ntn_number: data.ntn_number || record.ntn_number || "",
    sntn_number: data.sntn_number || record.sntn_number || "",
    addressLatitude:
      data.addressLatitude ||
      resolveCoordinateText(
        record.lat,
        record.latitude,
        record.address_lat,
        record.address_latitude
      ),
    addressLongitude:
      data.addressLongitude ||
      resolveCoordinateText(
        record.lng,
        record.longitude,
        record.address_lng,
        record.address_longitude
      ),
    industryType: data.industryType || record.industryType || "",
    typesOfGoods: safeArray(data.typesOfGoods).length
      ? safeArray(data.typesOfGoods)
      : safeArray(record.typesOfGoods)
        .map(resolveGoodsTypeId)
        .filter((value) => value !== null),
    status: data.status || record.status || "prospect",
    primaryContact: {
      ...base.primaryContact,
      ...(data.primaryContact || {}),
      name: data.primaryContact?.name || record.primaryContactPerson || "",
    },
    secondaryContacts: safeArray(data.secondaryContacts),
    noWarehouses: false,
    warehouses: ensureMinimumWarehouses(
      warehouseSource.map((item) => ({
        ...item,
        province: normalizeProvinceValue(item?.province),
        warehouseLatitude: resolveCoordinateText(
          item?.warehouseLatitude,
          item?.lat,
          item?.latitude,
          item?.warehouse_lat,
          item?.warehouse_latitude
        ),
        warehouseLongitude: resolveCoordinateText(
          item?.warehouseLongitude,
          item?.lng,
          item?.longitude,
          item?.warehouse_lng,
          item?.warehouse_longitude
        ),
      }))
    ),
    products: ensureMinimumProducts(
      productSource.map((item, index) => ({
        id: item?.id || `product-${index}`,
        name: cleanText(item?.name),
        unit: item?.unit_id ?? item?.unit ?? "",
        productType: cleanText(item?.productType ?? item?.product_type),
        productValue: cleanText(item?.productValue ?? item?.product_value),
        insuranceCoverType: cleanText(
          item?.insuranceCoverType ?? item?.insurance_cover_type
        ),
        insuranceStatus: cleanText(
          item?.insuranceStatus ?? item?.insurance_status
        ),
        insuranceCoverValue: cleanText(
          item?.insuranceCoverValue ?? item?.insurance_cover_value
        ),
      }))
    ),
    attachments: safeArray(data.attachments),
    payment: { ...base.payment, ...(data.payment || {}) },
    notes: data.notes || base.notes,
  };
};

const toCreateCustomerPayload = (values, unitOptions = []) => {
  const warehouses = safeArray(values.warehouses).filter(hasFilledWarehouse);
  const products = safeArray(values.products).filter(hasFilledProduct);

  const pocDetails = [
    values.primaryContact,
    ...safeArray(values.secondaryContacts),
  ]
    .filter(hasFilledContact)
    .map((item, index) => ({
      name: cleanText(item?.name),
      email: cleanText(item?.email),
      contact: cleanText(item?.contact),
      designation: cleanText(item?.designation),
      is_primary: index === 0,
    }));

  return {
    name: cleanText(values.customerName),
    contact: cleanText(values.contact),
    email: cleanText(values.email),
    status: cleanText(values.status).toLowerCase(),
    industry_type: cleanText(values.industryType),
    note: stripHtml(values.notes),
    poc_details: pocDetails,
    ntn_number: cleanText(values.ntn_number),
    sntn_number: cleanText(values.sntn_number),
    address: cleanText(values.address),
    cost_center_id: values.costCenterId ? Number(values.costCenterId) : null,
    has_warehouses: warehouses.length > 0,
    warehouses: warehouses.map((item) => ({
      warehouse_name: cleanText(item?.warehouseName),
      city: toSentenceCase(item?.city),
      contact: cleanText(item?.contact),
      contact_person_name: cleanText(item?.contactPersonName),
      contact_person_number: cleanText(item?.contactPersonNumber),
      address: cleanText(item?.warehouseAddress),
      lat: cleanText(item?.warehouseLatitude),
      lng: cleanText(item?.warehouseLongitude),
      province: normalizeProvinceValue(item?.province),
      status: cleanText(item?.status || "active").toLowerCase(),
    })),
    products: products.map((item) => ({
      name: cleanText(item?.name),
      unit: resolveOptionText(unitOptions, item?.unit),
      product_type: cleanText(item?.productType),
      product_value: toOptionalNumber(item?.productValue),
      insurance_cover_type: cleanText(item?.insuranceCoverType),
      insurance_status: cleanText(item?.insuranceStatus),
      insurance_cover_value: toOptionalNumber(item?.insuranceCoverValue),
    })),
    files: unique(
      safeArray(values.attachments)
        .map(resolveUploadedFilePath)
        .filter(Boolean)
    ),
    payment_method: cleanText(values.payment?.paymentMethod),
    payment_terms: cleanText(values.payment?.paymentTerm),
    billing_frequency: cleanText(values.payment?.billingFrequency),
    bank_name: cleanText(values.payment?.bank),
    account_title: cleanText(values.payment?.accountTitle),
    iban_number: cleanText(values.payment?.iban),
  };
};

const resolveAddressCoordinates = async (address) => {
  const normalizedAddress = cleanText(address);
  if (!normalizedAddress) return { lat: "", lng: "" };

  try {
    const results = await geocodeByAddress(normalizedAddress);
    const coordinates = await getLatLng(results[0]);

    return {
      lat: cleanText(coordinates?.lat),
      lng: cleanText(coordinates?.lng),
    };
  } catch (error) {
    return { lat: "", lng: "" };
  }
};

const withResolvedWarehouseCoordinates = async (values = {}) => {
  const warehouses = await Promise.all(
    safeArray(values.warehouses).map(async (warehouse) => {
      const existingLatitude = cleanText(warehouse?.warehouseLatitude);
      const existingLongitude = cleanText(warehouse?.warehouseLongitude);

      if (existingLatitude && existingLongitude) {
        return warehouse;
      }

      const coordinates = await resolveAddressCoordinates(warehouse?.warehouseAddress);

      return {
        ...warehouse,
        warehouseLatitude: existingLatitude || coordinates.lat,
        warehouseLongitude: existingLongitude || coordinates.lng,
      };
    })
  );

  return {
    ...values,
    warehouses,
  };
};

const stepSchemas = [
  Yup.object().shape({
    customerName: Yup.string().trim().required("Customer Name is required"),
    contact: buildPhoneNumberSchema("Contact is required"),
    email: Yup.string()
      .trim()
      .required("Email Address is required")
      .email("Invalid email address"),
    industryType: Yup.string().required("Industry Type is required"),
    status: Yup.string().required("Status is required"),
    ntn_number: Yup.string()
      .trim()
      .required("NTN Number is required")
      .matches(/^\d{7}-?\d{1}$/, "NTN must be in format 1234567-8 or 12345678"),
    sntn_number: Yup.string()
      .trim()
      .required("STRN Number is required")
      .matches(/^(\d{2}-\d{2}-\d{4}-\d{3}-\d{2}|\d{13})$/, "STRN must be in format 12-34-5678-901-23 or 1234567891011"),
    address: Yup.string().trim().required("Billing Address is required"),
    costCenterId: Yup.string().required("Cost Center is required"),
  }),
  Yup.object().shape({
    primaryContact: Yup.object().shape({
      name: Yup.string().trim().required("Primary contact name is required"),
      contact: buildPhoneNumberSchema("Primary contact number is required"),
      designation: Yup.string().required("Primary designation is required"),
      email: Yup.string()
        .trim()
        .required("Primary email is required")
        .email("Invalid email address"),
    }),
    secondaryContacts: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().trim().required("Secondary contact name is required"),
        contact: buildPhoneNumberSchema("Secondary contact number is required"),
        designation: Yup.string().required("Secondary designation is required"),
        email: Yup.string()
          .trim()
          .nullable()
          .transform((v, o) => (String(o || "").trim() ? v : null))
          .email("Invalid email address"),
      })
    ),
  }),
  Yup.object().shape({
    warehouses: Yup.array()
      .of(
        Yup.object().shape({
          warehouseName: Yup.string()
            .trim()
            .required("Location is required"),
          city: Yup.string().required("City is required"),
          province: Yup.string().nullable().notRequired(),
          contact: buildPhoneNumberSchema("Contact is required"),
          contactPersonName: Yup.string()
            .trim()
            .required("Contact Person Name is required"),
          contactPersonNumber: buildPhoneNumberSchema(
            "Contact Person Number is required"
          ),
          warehouseAddress: Yup.string()
            .trim()
            .required("Address is required"),
          status: Yup.string().required("Status is required"),
        })
      )
      .min(
        minimumWarehouseCount,
        `At least ${minimumWarehouseCount} locations are required`
      ),
  }),
  Yup.object().shape({
    products: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().trim().required("Product Name is required"),
          unit: Yup.mixed().test(
            "unit-required",
            "Unit is required",
            (value) => String(value ?? "").trim() !== ""
          ),
          productType: Yup.string().trim().required("Product Type is required"),
          productValue: Yup.number()
            .transform((value, originalValue) =>
              String(originalValue ?? "").trim() === "" ? undefined : value
            )
            .typeError("Product Value is required")
            .required("Product Value is required")
            .min(0, "Product Value cannot be negative"),
          insuranceCoverType: Yup.string()
            .trim()
            .required("Insurance Cover Type is required"),
          insuranceStatus: Yup.string()
            .trim()
            .required("Insurance Status is required"),
          insuranceCoverValue: Yup.number()
            .transform((value, originalValue) =>
              String(originalValue ?? "").trim() === "" ? undefined : value
            )
            .typeError("Insurance Cover Value is required")
            .required("Insurance Cover Value is required")
            .min(0, "Insurance Cover Value cannot be negative")
            .max(100, "Insurance Cover Value cannot exceed 100%"),
        })
      )
      .min(minimumProductCount, "At least one product is required"),
  }),
  Yup.object().shape({
    attachments: Yup.array(),
  }),
  Yup.object().shape({
    payment: Yup.object().shape({
      billingFrequency: Yup.string().required("Billing Frequency is required"),
      paymentMethod: Yup.string()
        .oneOf(["bank_transfer", "cash"], "Payment Method is required")
        .required("Payment Method is required"),
      paymentTerm: Yup.string().when("paymentMethod", {
        is: (method) => ["bank_transfer", "cash"].includes(method),
        then: (schema) => schema.required("Payment Term is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      bank: Yup.string().when("paymentMethod", {
        is: "bank_transfer",
        then: (schema) => schema.required("Bank is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      accountTitle: Yup.string().when("paymentMethod", {
        is: "bank_transfer",
        then: (schema) => schema.required("Account Title is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      iban: Yup.string().when("paymentMethod", {
        is: "bank_transfer",
        then: (schema) => schema.required("IBAN / Account Number is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  }),
];

const editCustomerSchema = stepSchemas[0].concat(stepSchemas[5]);

const customerLocationTableSchema = Yup.object().shape({
  warehouseName: Yup.string().trim().required("Location is required"),
  city: Yup.string().required("City is required"),
  province: Yup.string().nullable().notRequired(),
  contact: buildPhoneNumberSchema("Contact is required"),
  contactPersonName: Yup.string()
    .trim()
    .required("Contact Person Name is required"),
  contactPersonNumber: buildPhoneNumberSchema(
    "Contact Person Number is required"
  ),
  warehouseAddress: Yup.string().trim().required("Address is required"),
  status: Yup.string().required("Status is required"),
});

const customerProductTableSchema = Yup.object().shape({
  name: Yup.string().trim().required("Product Name is required"),
  unit: Yup.mixed().test(
    "unit-required",
    "Unit is required",
    (value) => String(value ?? "").trim() !== ""
  ),
  productType: Yup.string().trim().required("Product Type is required"),
  productValue: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue ?? "").trim() === "" ? undefined : value
    )
    .typeError("Product Value is required")
    .required("Product Value is required")
    .min(0, "Product Value cannot be negative"),
  insuranceCoverType: Yup.string()
    .trim()
    .required("Insurance Cover Type is required"),
  insuranceStatus: Yup.string()
    .trim()
    .required("Insurance Status is required"),
  insuranceCoverValue: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue ?? "").trim() === "" ? undefined : value
    )
    .typeError("Insurance Cover Value is required")
    .required("Insurance Cover Value is required")
    .min(0, "Insurance Cover Value cannot be negative")
    .max(100, "Insurance Cover Value cannot exceed 100%"),
});

const customerSecondaryContactTableSchema = Yup.object().shape({
  name: Yup.string().trim().required("Secondary contact name is required"),
  contact: buildPhoneNumberSchema("Secondary contact number is required"),
  designation: Yup.string().required("Secondary designation is required"),
  email: Yup.string()
    .trim()
    .nullable()
    .transform((v, o) => (String(o || "").trim() ? v : null))
    .email("Invalid email address"),
});

const mapCustomerDetailToFormValues = (detail) => {
  const base = defaultValues();
  if (!detail || typeof detail !== "object") return base;

  const poc = safeArray(detail?.poc || detail?.poc_details);
  const primaryPoc = poc.find((entry) => Boolean(entry?.is_primary)) || poc[0] || {};
  const secondaryContacts = poc
    .filter((entry) => entry !== primaryPoc)
    .map((entry, index) => ({
      id: entry?.id || `secondary-${index}`,
      name: toText(entry?.name),
      contact: toText(entry?.contact),
      designation: toText(entry?.designation),
      email: toText(entry?.email),
    }));

  const customerGoods = safeArray(detail?.customer_goods || detail?.goods);
  const goodsForForm = customerGoods
    .map((goods) => {
      const id = Number(goods?.id ?? goods?.goods_id ?? goods);
      if (Number.isFinite(id) && id > 0) return id;
      return resolveGoodsValue(goods) || null;
    })
    .filter(Boolean);

  const warehouses = safeArray(detail?.warehouses).map((warehouse, index) => ({
    id: warehouse?.id || `warehouse-${index}`,
    warehouseName: toText(warehouse?.warehouse_name ?? warehouse?.name),
    city: normalize(warehouse?.city),
    contact: toText(warehouse?.contact),
    contactPersonName: toText(warehouse?.contact_person_name),
    contactPersonNumber: toText(warehouse?.contact_person_number),
    warehouseAddress: toText(warehouse?.address ?? warehouse?.warehouse_address),
    warehouseLatitude: resolveCoordinateText(
      warehouse?.lat,
      warehouse?.latitude,
      warehouse?.warehouse_lat,
      warehouse?.warehouse_latitude
    ),
    warehouseLongitude: resolveCoordinateText(
      warehouse?.lng,
      warehouse?.longitude,
      warehouse?.warehouse_lng,
      warehouse?.warehouse_longitude
    ),
    province: normalizeProvinceValue(warehouse?.province),
    status: resolveWarehouseStatus(
      warehouse?.status ?? warehouse?.is_active ?? warehouse?.status_id
    ),
    typesOfGoods: safeArray(warehouse?.warehouse_goods || warehouse?.goods)
      .map((goods) => {
        const id = Number(goods?.id ?? goods?.goods_id ?? goods);
        if (Number.isFinite(id) && id > 0) return id;
        return resolveGoodsValue(goods) || null;
      })
      .filter(Boolean),
  }));
  const products = safeArray(
    detail?.customer_products || detail?.products
  ).map((product, index) => ({
    id: product?.id || `product-${index}`,
    name: toText(product?.name),
    unit: product?.unit_id ?? product?.unit ?? "",
    productType: toText(product?.product_type),
    productValue: toText(product?.product_value),
    insuranceCoverType: toText(product?.insurance_cover_type),
    insuranceStatus: toText(product?.insurance_status),
    insuranceCoverValue: toText(product?.insurance_cover_value),
  }));

  const status = resolveStatus(detail?.status) || "prospect";
  const customerId = toText(detail?.customer_id) || toText(detail?.id);

  return {
    ...base,
    customerId,
    customerName: toText(detail?.name),
    contact: toText(detail?.contact),
    email: toText(detail?.email),
    headquarter: normalize(detail?.headquarter),
    address: toText(detail?.address),
    ntn_number: toText(detail?.ntn_number),
    sntn_number: toText(detail?.sntn_number),
    addressLatitude: resolveCoordinateText(
      detail?.lat,
      detail?.latitude,
      detail?.address_lat,
      detail?.address_latitude
    ),
    addressLongitude: resolveCoordinateText(
      detail?.lng,
      detail?.longitude,
      detail?.address_lng,
      detail?.address_longitude
    ),
    industryType: toText(detail?.industry_type),
    typesOfGoods: goodsForForm,
    status,
    primaryContact: {
      ...base.primaryContact,
      name: toText(primaryPoc?.name),
      contact: toText(primaryPoc?.contact),
      designation: toText(primaryPoc?.designation),
      email: toText(primaryPoc?.email),
    },
    secondaryContacts,
    noWarehouses: false,
    warehouses,
    products,
    attachments: safeArray(detail?.files),
    payment: {
      ...base.payment,
      billingFrequency: toText(detail?.billing_frequency),
      paymentMethod: toText(detail?.payment_method),
      paymentTerm: toText(detail?.payment_terms),
      bank: toText(detail?.bank_name),
      accountTitle: toText(detail?.account_title),
      iban: toText(detail?.iban_number),
    },
    notes: toText(detail?.note),
    costCenterId: detail?.cost_center_id ? String(detail.cost_center_id) : "",
  };
};

const CustomerDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeCustomerId } = useParams();
  const dispatch = useDispatch();
  const canCreateCustomers = hasPermission({
    permissionSlug: CustomersPermission.Create,
    menuSlug: CustomersSubModules.Customers,
    moduleSlug: AppMainModules.customers,
  });
  const canUpdateCustomers = hasPermission({
    permissionSlug: CustomersPermission.Update,
    menuSlug: CustomersSubModules.Customers,
    moduleSlug: AppMainModules.customers,
  });

  const {
    banksDropdownData = [],
    partsUnitsDropdownData = [],
    transporterCitiesDropdownData = [],
    usersDropdownData = [],
    costCenterDropdownData = [],
    loading: dropdownLoading,
  } = useSelector((state) => state.dropdowns);
  const { createCustomerLoading, updateCustomerLoading } = useSelector(
    (state) => state.customer
  );

  const isEditMode = true;
  const editRecordFromState = location.state?.record || null;
  const editRecordId =
    routeCustomerId ||
    location.state?.id ||
    editRecordFromState?.recordId ||
    editRecordFromState?.id ||
    null;

  const [currentStep, setCurrentStep] = useState(0);
  const [activeDetailTab, setActiveDetailTab] = useState("general");
  const [initialValues, setInitialValues] = useState(defaultValues());
  const [customerDetailRecord, setCustomerDetailRecord] = useState(null);
  const [customerRoutes, setCustomerRoutes] = useState([]);
  const [customerRoutesLoading, setCustomerRoutesLoading] = useState(false);
  const [customerRouteLocationOptions, setCustomerRouteLocationOptions] = useState([]);
  const [customerRouteLocationOptionsLoading, setCustomerRouteLocationOptionsLoading] =
    useState(false);
  const [customerRouteLogsPage, setCustomerRouteLogsPage] = useState(1);
  const [customerRouteLogsLimit, setCustomerRouteLogsLimit] = useState(10);
  const [customerRouteLogsSearchText, setCustomerRouteLogsSearchText] = useState("");
  const [customerRouteLogsSearchFilters, setCustomerRouteLogsSearchFilters] = useState({});
  const [customerRouteHistoryModalOpen, setCustomerRouteHistoryModalOpen] =
    useState(false);
  const [viewingCustomerRoute, setViewingCustomerRoute] = useState(null);
  const [customerRouteHistoryLoading, setCustomerRouteHistoryLoading] =
    useState(false);
  const [customerRouteHistoryRows, setCustomerRouteHistoryRows] = useState([]);
  const [customerRouteHistoryPage, setCustomerRouteHistoryPage] = useState(1);
  const [customerRouteHistoryLimit, setCustomerRouteHistoryLimit] = useState(10);
  const [customerRouteHistoryTotalItems, setCustomerRouteHistoryTotalItems] =
    useState(0);
  const [customerRouteHistoryTotalPages, setCustomerRouteHistoryTotalPages] =
    useState(1);
  const [customerRouteModalOpen, setCustomerRouteModalOpen] = useState(false);
  const [customerRouteModalMode, setCustomerRouteModalMode] = useState("create");
  const [isChangedAtDirty, setIsChangedAtDirty] = useState(false);
  const [editingCustomerRoute, setEditingCustomerRoute] = useState(null);
  const [customerRouteSubmitting, setCustomerRouteSubmitting] = useState(false);
  const [customerRouteForm, setCustomerRouteForm] = useState(createCustomerRouteForm());
  const [customerRouteErrors, setCustomerRouteErrors] = useState({});
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [locationDraft, setLocationDraft] = useState(createCustomerLocationDraft);
  const [locationDraftErrors, setLocationDraftErrors] = useState({});
  const [locationDeleteModalOpen, setLocationDeleteModalOpen] = useState(false);
  const [locationDeleteTarget, setLocationDeleteTarget] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productDraft, setProductDraft] = useState(createProduct);
  const [productDraftErrors, setProductDraftErrors] = useState({});
  const [productDeleteModalOpen, setProductDeleteModalOpen] = useState(false);
  const [productDeleteTarget, setProductDeleteTarget] = useState(null);
  const [productSubmitting, setProductSubmitting] = useState(false);
  const [locationSubmitting, setLocationSubmitting] = useState(false);

  // Product Rate History & Edit States
  const [productHistoryModalOpen, setProductHistoryModalOpen] = useState(false);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [productHistoryLoading, setProductHistoryLoading] = useState(false);
  const [productHistoryRows, setProductHistoryRows] = useState([]);
  const [productHistoryPage, setProductHistoryPage] = useState(1);
  const [productHistoryLimit, setProductHistoryLimit] = useState(10);
  const [productHistoryTotalItems, setProductHistoryTotalItems] = useState(0);
  const [productHistoryTotalPages, setProductHistoryTotalPages] = useState(1);

  const [productRateModalOpen, setProductRateModalOpen] = useState(false);
  const [editingProductRate, setEditingProductRate] = useState(null);
  const [productRateSubmitting, setProductRateSubmitting] = useState(false);
  const [productRateForm, setProductRateForm] = useState({ productValue: "", changedAt: "" });
  const [productRateErrors, setProductRateErrors] = useState({});

  const [showSecondaryForm, setShowSecondaryForm] = useState(false);
  const [editingSecondaryId, setEditingSecondaryId] = useState(null);
  const [secondaryDraft, setSecondaryDraft] = useState(createSecondary);
  const [secondaryDraftErrors, setSecondaryDraftErrors] = useState({});
  const [secondaryDeleteModalOpen, setSecondaryDeleteModalOpen] = useState(false);
  const [secondaryDeleteTarget, setSecondaryDeleteTarget] = useState(null);
  const canSubmitChanges = isEditMode ? canUpdateCustomers : canCreateCustomers;

  const resolvedCustomerEntityId = useMemo(() => {
    const candidates = [
      customerDetailRecord?.id,
      customerDetailRecord?.customer_id,
      editRecordId,
    ];

    for (const candidate of candidates) {
      const nextId = extractPositiveNumber(candidate);
      if (nextId) return nextId;
    }

    return null;
  }, [customerDetailRecord?.id, customerDetailRecord?.customer_id, editRecordId]);

  const [locationsExportLoading, setLocationsExportLoading] = useState(null);
  const [productsExportLoading, setProductsExportLoading] = useState(null);
  const [productRatesExportLoading, setProductRatesExportLoading] = useState(null);

  const downloadExportedFile = (filePath, fileName) => {
    const fileUrl = /^https?:\/\//i.test(filePath)
      ? filePath
      : `${process.env.REACT_APP_API_FILES_URL}${filePath}`;
    const link = document.createElement("a");
    link.href = fileUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLocationsExport = async (type, currentWarehouses, currentCustomerName) => {
    if (locationsExportLoading) return;
    const targetCustomerId =
      resolvedCustomerEntityId || editRecordId || customerDetailRecord?.id;
    if (!targetCustomerId) {
      errorNotify("Customer ID is missing");
      return;
    }
    setLocationsExportLoading(type);
    try {
      const payload = {
        customerName:
          currentCustomerName || customerDetailRecord?.name || "Customer",
        items: safeArray(currentWarehouses),
      };
      const response =
        type === "pdf"
          ? await apiService.exportCustomerLocationsPdf(
              targetCustomerId,
              payload
            )
          : await apiService.exportCustomerLocationsExcel(
              targetCustomerId,
              payload
            );

      const responseData = response?.data ?? response;
      const filePath =
        responseData?.url ||
        responseData?.file ||
        (typeof responseData === "string" ? responseData : null);

      if (!filePath) {
        throw new Error(
          `${type.toUpperCase()} was generated but no file was returned`
        );
      }
      const safeName = (payload.customerName || "Customer").replace(
        /[^a-zA-Z0-9_-]/g,
        "_"
      );
      downloadExportedFile(
        filePath,
        `${safeName}-locations.${type === "pdf" ? "pdf" : "xlsx"}`
      );
      successNotify(
        `Customer locations ${type.toUpperCase()} downloaded successfully`
      );
    } catch (error) {
      const msg = typeof error === "string" ? error : error?.message;
      errorNotify(msg || `Failed to download locations ${type.toUpperCase()}`);
    } finally {
      setLocationsExportLoading(null);
    }
  };

  const handleProductsExport = async (type, currentProducts, currentCustomerName) => {
    if (productsExportLoading) return;
    const targetCustomerId =
      resolvedCustomerEntityId || editRecordId || customerDetailRecord?.id;
    if (!targetCustomerId) {
      errorNotify("Customer ID is missing");
      return;
    }
    setProductsExportLoading(type);
    try {
      const payload = {
        customerName:
          currentCustomerName || customerDetailRecord?.name || "Customer",
        items: safeArray(currentProducts),
      };
      const response =
        type === "pdf"
          ? await apiService.exportCustomerProductsPdf(
              targetCustomerId,
              payload
            )
          : await apiService.exportCustomerProductsExcel(
              targetCustomerId,
              payload
            );

      const responseData = response?.data ?? response;
      const filePath =
        responseData?.url ||
        responseData?.file ||
        (typeof responseData === "string" ? responseData : null);

      if (!filePath) {
        throw new Error(
          `${type.toUpperCase()} was generated but no file was returned`
        );
      }
      const safeName = (payload.customerName || "Customer").replace(
        /[^a-zA-Z0-9_-]/g,
        "_"
      );
      downloadExportedFile(
        filePath,
        `${safeName}-products.${type === "pdf" ? "pdf" : "xlsx"}`
      );
      successNotify(
        `Customer products ${type.toUpperCase()} downloaded successfully`
      );
    } catch (error) {
      const msg = typeof error === "string" ? error : error?.message;
      errorNotify(msg || `Failed to download products ${type.toUpperCase()}`);
    } finally {
      setProductsExportLoading(null);
    }
  };

  const handleProductRatesExport = async (type) => {
    if (productRatesExportLoading) return;
    const targetProductId = extractPositiveNumber(viewingProduct?.id);
    if (!targetProductId) {
      errorNotify("Product ID is missing");
      return;
    }

    setProductRatesExportLoading(type);
    try {
      const payload = {
        product_id: targetProductId,
      };

      const response =
        type === "pdf"
          ? await apiService.exportProductRatesPdf(payload)
          : await apiService.exportProductRatesExcel(payload);

      const responseData = response?.data ?? response;
      const filePath =
        responseData?.url ||
        responseData?.file ||
        (typeof responseData === "string" ? responseData : null);

      if (!filePath) {
        throw new Error(
          `${type.toUpperCase()} was generated but no file was returned`
        );
      }

      const safeProductName = (viewingProduct?.name || "Product_Rate_History").replace(
        /[^a-zA-Z0-9_-]/g,
        "_"
      );

      downloadExportedFile(
        filePath,
        `${safeProductName}-rate-history.${type === "pdf" ? "pdf" : "xlsx"}`
      );
      successNotify(
        `Product rate history ${type.toUpperCase()} downloaded successfully`
      );
    } catch (error) {
      const msg = typeof error === "string" ? error : error?.message;
      errorNotify(msg || `Failed to download product rate history ${type.toUpperCase()}`);
    } finally {
      setProductRatesExportLoading(null);
    }
  };

  useEffect(() => {
    dispatch(fetchBanksDropdown());
    dispatch(fetchPartsUnitsDropdown());
    dispatch(fetchTransporterCitiesDropdown());
    dispatch(fetchUsersDropdown());
    dispatch(fetchCostCenterDropdown());
  }, [dispatch]);

  useEffect(() => {
    let ignore = false;

    const loadInitialValues = async () => {
      let nextValues =
        isEditMode && editRecordFromState
          ? toFormValues(editRecordFromState)
          : defaultValues();
      let nextCustomerDetailRecord = editRecordFromState || null;

      if (isEditMode && editRecordId) {
        try {
          const response = await dispatch(fetchCustomerDetail(editRecordId)).unwrap();
          const detail = response?.data || response || null;
          if (detail) {
            nextValues = mapCustomerDetailToFormValues(detail);
            nextCustomerDetailRecord = detail;
          }
        } catch (error) {
          if (!editRecordFromState) {
            errorNotify(error || "Failed to fetch customer detail");
          }
        }
      } else {
        try {
          const initialListResponse = await apiService.getCustomersPaginated({
            page: 1,
            limit: 1000,
          });

          let customerRows = safeArray(
            initialListResponse?.data?.results || initialListResponse?.results
          );
          const totalItems = Number(
            initialListResponse?.data?.meta?.totalItems ||
            initialListResponse?.meta?.totalItems
          );

          if (Number.isFinite(totalItems) && totalItems > customerRows.length) {
            const fullListResponse = await apiService.getCustomersPaginated({
              page: 1,
              limit: totalItems,
            });
            customerRows = safeArray(
              fullListResponse?.data?.results || fullListResponse?.results
            );
          }

          nextValues.customerId = generateNextCustomerId(customerRows);
        } catch (error) {
          nextValues.customerId = generateNextCustomerId();
        }
      }

      if (!ignore) {
        setInitialValues(nextValues);
        setCurrentStep(0);
        setActiveDetailTab("general");
        setCustomerDetailRecord(nextCustomerDetailRecord);
        setCustomerRouteForm(createCustomerRouteForm());
        setCustomerRouteErrors({});
        setCustomerRouteLogsPage(1);
        setCustomerRouteLogsLimit(10);
        setCustomerRouteLogsSearchText("");
        setCustomerRouteLogsSearchFilters({});
        setCustomerRouteHistoryModalOpen(false);
        setViewingCustomerRoute(null);
        setCustomerRouteHistoryRows([]);
        setCustomerRouteHistoryPage(1);
        setCustomerRouteHistoryLimit(10);
        setCustomerRouteHistoryTotalItems(0);
        setCustomerRouteHistoryTotalPages(1);
        setCustomerRouteModalOpen(false);
        setCustomerRouteModalMode("create");
        setEditingCustomerRoute(null);
        setShowLocationForm(false);
        setEditingLocationId(null);
        setLocationDraft(createCustomerLocationDraft());
        setLocationDraftErrors({});
        setLocationDeleteModalOpen(false);
        setLocationDeleteTarget(null);
        setShowProductForm(false);
        setEditingProductId(null);
        setProductDraft(createProduct());
        setProductDraftErrors({});
        setProductDeleteModalOpen(false);
        setProductDeleteTarget(null);
        setShowSecondaryForm(false);
        setEditingSecondaryId(null);
        setSecondaryDraft(createSecondary());
        setSecondaryDraftErrors({});
        setSecondaryDeleteModalOpen(false);
        setSecondaryDeleteTarget(null);
      }
    };

    loadInitialValues();

    return () => {
      ignore = true;
    };
  }, [dispatch, isEditMode, editRecordFromState, editRecordId]);

  const bankOptions = useMemo(
    () => (banksDropdownData?.length ? banksDropdownData : fallbackBankOptions),
    [banksDropdownData]
  );

  const getOptionLabel = (item) =>
    String(
      item?.label ?? item?.name ?? item?.title ?? item?.city ?? item?.value ?? ""
    ).trim();

  const warehouseCityOptions = useMemo(() => {
    const source = transporterCitiesDropdownData?.length
      ? transporterCitiesDropdownData
      : cityOptions;
    return source
      .map((item) => {
        const label = getOptionLabel(item);
        if (!label) return null;
        return { label, value: normalize(label) };
      })
      .filter(Boolean);
  }, [transporterCitiesDropdownData]);

  const partUnitOptions = useMemo(
    () => safeArray(partsUnitsDropdownData),
    [partsUnitsDropdownData]
  );
  const usersDropdownOptions = useMemo(
    () => safeArray(usersDropdownData),
    [usersDropdownData]
  );
  const userNameLookup = useMemo(
    () =>
      usersDropdownOptions.reduce((acc, user) => {
        const keys = [
          user?.value,
          user?.id,
          user?.user_id,
          user?.employee_id,
        ]
          .map((item) => cleanText(item))
          .filter(Boolean);
        const label = firstFilled(user?.label, resolveUserName(user));

        if (!label) return acc;
        keys.forEach((key) => {
          acc[key] = label;
        });
        return acc;
      }, {}),
    [usersDropdownOptions]
  );

  const pickOption = (options, selectedValue) => {
    const selected = String(selectedValue || "");
    return (
      options.find(
        (option) =>
          normalize(option?.value) === normalize(selected) ||
          normalize(option?.label) === normalize(selected)
      ) || null
    );
  };

  const contactTableColumns = useMemo(
    () => [
      {
        header: "Name",
        align: "left",
        render: (row) => (
          <>
            {row.name || "--"}
            {row.isPrimary && (
              <span
                className="badge bg-primary text-white ms-2"
                style={{ fontSize: "11px", padding: "3px 6px" }}
              >
                Primary
              </span>
            )}
          </>
        ),
      },
      {
        header: "Contact",
        align: "left",
        render: (row) => row.contact || "--",
      },
      {
        header: "Designation",
        align: "left",
        render: (row) =>
          resolveOptionText(designationOptions, row.designation) || "--",
      },
      {
        header: "Email Address",
        align: "left",
        render: (row) => row.email || "--",
      },
    ],
    []
  );

  const locationTableColumns = useMemo(
    () => [
      {
        header: "Location Id",
        align: "left",
        render: (row) =>
          row.id ? (
            <span className="badge bg-light text-secondary border" title={`Location ID: ${row.id}`}>
             {row.id}
            </span>
          ) : (
            "--"
          ),
      },
      {
        header: "Location Name",
        align: "left",
        render: (row) => row.warehouseName || "--",
      },
      {
        header: "Status",
        align: "left",
        render: (row) => {
          const resolved = resolveWarehouseStatus(
            row.status ?? row.is_active ?? row.status_id
          );
          if (!resolved) return "--";
          const isInactive = resolved === "inactive";
          return (
            <span
              className={`badge ${isInactive ? "bg-danger" : "bg-success"} text-white text-capitalize`}
            >
              {isInactive ? "Inactive" : "Active"}
            </span>
          );
        },
      },
      {
        header: "City",
        align: "left",
        render: (row) =>
          resolveOptionText(warehouseCityOptions, row.city) || "--",
      },
      {
        header: "Contact",
        align: "left",
        render: (row) => row.contact || "--",
      },
      {
        header: "Person Name",
        align: "left",
        render: (row) => row.contactPersonName || "--",
      },
      {
        header: "Person Contact",
        align: "left",
        render: (row) => row.contactPersonNumber || "--",
      },
      {
        header: "Address",
        align: "left",
        render: (row) => row.warehouseAddress || "--",
      },
      
    ],
    [warehouseCityOptions]
  );

  const productTableColumns = useMemo(
    () => [
      {
        header: "Product Name",
        align: "left",
        render: (row) => row.name || "--",
      },
      {
        header: "Unit",
        align: "left",
        render: (row) => resolveOptionText(partUnitOptions, row.unit) || "--",
      },
      {
        header: "Product Type",
        align: "left",
        render: (row) =>
          resolveOptionText(productTypeOptions, row.productType) || "--",
      },
      {
        header: "Product Value",
        align: "left",
        render: (row) => row.productValue || "--",
      },
      {
        header: "Insurance Cover Type",
        align: "left",
        render: (row) =>
          resolveOptionText(
            insuranceCoverTypeOptions,
            row.insuranceCoverType
          ) || "--",
      },
      {
        header: "Insurance Status",
        align: "left",
        render: (row) =>
          resolveOptionText(insuranceStatusOptions, row.insuranceStatus) ||
          "--",
      },
      {
        header: "Insurance Cover Value",
        align: "left",
        render: (row) => row.insuranceCoverValue || "--",
      },
    ],
    [partUnitOptions]
  );

  const loadCustomerRoutes = useCallback(async (customerId, { notifyOnError = false } = {}) => {
    const nextCustomerId = extractPositiveNumber(customerId);

    if (!nextCustomerId) {
      setCustomerRoutes([]);
      return;
    }

    setCustomerRoutesLoading(true);

    try {
      const response = await apiService.getCustomerRoutesList(nextCustomerId);
      setCustomerRoutes(resolveCustomerRouteList(response));
    } catch (error) {
      setCustomerRoutes([]);
      if (notifyOnError) {
        errorNotify(error || "Failed to fetch customer routes");
      }
    } finally {
      setCustomerRoutesLoading(false);
    }
  }, []);

  const loadCustomerRouteLocationOptions = useCallback(
    async (customerId, { notifyOnError = false } = {}) => {
      const nextCustomerId = extractPositiveNumber(customerId);

      if (!nextCustomerId) {
        setCustomerRouteLocationOptions([]);
        setCustomerRouteLocationOptionsLoading(false);
        return;
      }

      setCustomerRouteLocationOptions([]);
      setCustomerRouteLocationOptionsLoading(true);

      try {
        const response = await apiService.getCustomerRouteLocationDropdown(
          nextCustomerId
        );
        setCustomerRouteLocationOptions(
          resolveCustomerRouteLocationOptions(response)
        );
      } catch (error) {
        setCustomerRouteLocationOptions([]);
        if (notifyOnError) {
          errorNotify(error || "Failed to fetch customer route locations");
        }
      } finally {
        setCustomerRouteLocationOptionsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!isEditMode || !resolvedCustomerEntityId) {
      setCustomerRoutes([]);
      return;
    }

    loadCustomerRoutes(resolvedCustomerEntityId);
  }, [isEditMode, resolvedCustomerEntityId, loadCustomerRoutes]);

  useEffect(() => {
    if (!isEditMode || !resolvedCustomerEntityId) {
      setCustomerRouteLocationOptions([]);
      setCustomerRouteLocationOptionsLoading(false);
      return;
    }

    loadCustomerRouteLocationOptions(resolvedCustomerEntityId);
  }, [
    isEditMode,
    resolvedCustomerEntityId,
    loadCustomerRouteLocationOptions,
  ]);

  useEffect(() => {
    setCustomerRouteLogsPage(1);
  }, [customerRouteLogsSearchText, customerRouteLogsLimit, customerRouteLogsSearchFilters]);

  const loadCustomerRouteHistory = useCallback(
    async ({
      customerRouteId,
      page = customerRouteHistoryPage,
      limit = customerRouteHistoryLimit,
    } = {}) => {
      const nextCustomerRouteId = extractPositiveNumber(customerRouteId);

      if (!nextCustomerRouteId) {
        setCustomerRouteHistoryRows([]);
        setCustomerRouteHistoryTotalItems(0);
        setCustomerRouteHistoryTotalPages(1);
        return;
      }

      setCustomerRouteHistoryLoading(true);

      try {
        const response = await apiService.getCustomerRouteRateLogsPaginated({
          customer_route_id: nextCustomerRouteId,
          page,
          limit,
        });

        const payload = response?.data || response || {};
        const results = safeArray(payload?.results);
        const meta = payload?.meta || {};

        setCustomerRouteHistoryRows(results);
        console.log("Customer Route History Logs Results:", results);
        setCustomerRouteHistoryTotalItems(
          Number(payload?.total ?? meta?.totalItems ?? results.length) || 0
        );
        setCustomerRouteHistoryTotalPages(
          Number(meta?.totalPages || payload?.totalPages || 1) || 1
        );
      } catch (error) {
        setCustomerRouteHistoryRows([]);
        setCustomerRouteHistoryTotalItems(0);
        setCustomerRouteHistoryTotalPages(1);
        errorNotify(error || "Failed to fetch customer route history");
      } finally {
        setCustomerRouteHistoryLoading(false);
      }
    },
    [customerRouteHistoryPage, customerRouteHistoryLimit]
  );

  useEffect(() => {
    if (!customerRouteHistoryModalOpen) return;

    const nextCustomerRouteId = extractPositiveNumber(
      viewingCustomerRoute?.customerRouteId
    );
    if (!nextCustomerRouteId) return;

    loadCustomerRouteHistory({
      customerRouteId: nextCustomerRouteId,
      page: customerRouteHistoryPage,
      limit: customerRouteHistoryLimit,
    });
  }, [
    customerRouteHistoryModalOpen,
    viewingCustomerRoute?.customerRouteId,
    customerRouteHistoryPage,
    customerRouteHistoryLimit,
    loadCustomerRouteHistory,
  ]);

  const loadProductHistory = useCallback(
    async ({
      productId,
      page = productHistoryPage,
      limit = productHistoryLimit,
    } = {}) => {
      const nextProductId = extractPositiveNumber(productId);

      if (!nextProductId) {
        setProductHistoryRows([]);
        setProductHistoryTotalItems(0);
        setProductHistoryTotalPages(1);
        return;
      }

      setProductHistoryLoading(true);

      try {
        const response = await apiService.getProductRatesPaginated({
          product_id: nextProductId,
          page,
          limit,
        });

        const payload = response?.data || response || {};
        const results = safeArray(payload?.results);
        const meta = payload?.meta || {};

        setProductHistoryRows(results);
        console.log("Product Rate History Logs Results:", results);
        setProductHistoryTotalItems(
          Number(payload?.total ?? meta?.totalItems ?? results.length) || 0
        );
        setProductHistoryTotalPages(
          Number(meta?.totalPages || payload?.totalPages || 1) || 1
        );
      } catch (error) {
        setProductHistoryRows([]);
        setProductHistoryTotalItems(0);
        setProductHistoryTotalPages(1);
        errorNotify(error || "Failed to fetch product rate history");
      } finally {
        setProductHistoryLoading(false);
      }
    },
    [productHistoryPage, productHistoryLimit]
  );

  useEffect(() => {
    if (!productHistoryModalOpen) return;

    const nextProductId = extractPositiveNumber(viewingProduct?.id);
    if (!nextProductId) return;

    loadProductHistory({
      productId: nextProductId,
      page: productHistoryPage,
      limit: productHistoryLimit,
    });
  }, [
    productHistoryModalOpen,
    viewingProduct?.id,
    productHistoryPage,
    productHistoryLimit,
    loadProductHistory,
  ]);

  const loadCustomerDetail = useCallback(async () => {
    if (!isEditMode || !editRecordId) return;

    try {
      const response = await dispatch(fetchCustomerDetail(editRecordId)).unwrap();
      const detail = response?.data || response || null;
      if (detail) {
        setInitialValues(mapCustomerDetailToFormValues(detail));
        setCustomerDetailRecord(detail);
      }
    } catch (error) {
      errorNotify(error || "Failed to reload customer details");
    }
  }, [isEditMode, editRecordId, dispatch]);

  const closeProductRateModal = () => {
    setProductRateModalOpen(false);
    setEditingProductRate(null);
    setProductRateForm({ productValue: "", changedAt: "" });
    setProductRateErrors({});
  };

  const openEditProductRateModal = (productRow) => {
    setEditingProductRate(productRow);
    setProductRateForm({
      productValue: "",
      changedAt: "",
    });
    setProductRateErrors({});
    setProductRateModalOpen(true);
  };

  const validateProductRateForm = () => {
    const errors = {};

    if (cleanText(productRateForm.productValue) === "") {
      errors.productValue = "Product value is required";
    } else if (!Number.isFinite(Number(productRateForm.productValue))) {
      errors.productValue = "Product value must be numeric";
    }

    if (!cleanText(productRateForm.changedAt)) {
      errors.changedAt = "Changed date and time is required";
    }

    return errors;
  };

  const handleSubmitProductRate = async () => {
    const nextErrors = validateProductRateForm();
    setProductRateErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      return;
    }

    setProductRateSubmitting(true);

    try {
      await apiService.updateProductRate({
        product_id: Number(editingProductRate?.id),
        customer_id: Number(resolvedCustomerEntityId),
        price: Number(productRateForm.productValue),
        changed_at: productRateForm.changedAt
          ? moment(productRateForm.changedAt).format("YYYY-MM-DD")
          : "",
      });

      successNotify("Product rate updated successfully");
      closeProductRateModal();
      await loadCustomerDetail();
    } catch (error) {
      errorNotify(error || "Failed to update product rate");
    } finally {
      setProductRateSubmitting(false);
    }
  };

  const validateCustomerRouteForm = () => {
    const errors = {};
    const isEditingRoute = customerRouteModalMode === "edit";

    if (!resolvedCustomerEntityId) {
      errors.customerId = "Customer id is not available";
    }

    if (
      isEditingRoute &&
      !extractPositiveNumber(editingCustomerRoute?.customerRouteId)
    ) {
      errors.customerId = "Customer route id is not available";
    }

    if (!isEditingRoute && !cleanText(customerRouteForm.fromWarehouseId)) {
      errors.fromWarehouseId = "From location is required";
    }

    if (!isEditingRoute && !cleanText(customerRouteForm.toWarehouseId)) {
      errors.toWarehouseId = "To location is required";
    }

    if (
      !isEditingRoute &&
      cleanText(customerRouteForm.fromWarehouseId) &&
      cleanText(customerRouteForm.fromWarehouseId) ===
      cleanText(customerRouteForm.toWarehouseId)
    ) {
      errors.toWarehouseId = "From and To locations must be different";
    }

    if (cleanText(customerRouteForm.locationRate) === "") {
      errors.locationRate = "Location rate is required";
    } else if (!Number.isFinite(Number(customerRouteForm.locationRate))) {
      errors.locationRate = "Location rate must be numeric";
    }

    if (!isEditingRoute && cleanText(customerRouteForm.fixDistance) === "") {
      errors.fixDistance = "Fixed distance is required";
    } else if (
      !isEditingRoute &&
      !Number.isFinite(Number(customerRouteForm.fixDistance))
    ) {
      errors.fixDistance = "Fixed distance must be numeric";
    }

    if (isEditingRoute && !cleanText(customerRouteForm.changedAt)) {
      errors.changedAt = "Changed date and time is required";
    }

    return errors;
  };

  const closeCustomerRouteModal = () => {
    setCustomerRouteModalOpen(false);
    setCustomerRouteModalMode("create");
    setEditingCustomerRoute(null);
    setCustomerRouteForm(createCustomerRouteForm());
    setCustomerRouteErrors({});
    setIsChangedAtDirty(false);
  };

  const closeCustomerRouteHistoryModal = () => {
    setCustomerRouteHistoryModalOpen(false);
    setViewingCustomerRoute(null);
    setCustomerRouteHistoryRows([]);
    setCustomerRouteHistoryPage(1);
    setCustomerRouteHistoryLimit(10);
    setCustomerRouteHistoryTotalItems(0);
    setCustomerRouteHistoryTotalPages(1);
  };

  const closeProductHistoryModal = () => {
    setProductHistoryModalOpen(false);
    setViewingProduct(null);
    setProductHistoryRows([]);
    setProductHistoryPage(1);
    setProductHistoryLimit(10);
    setProductHistoryTotalItems(0);
    setProductHistoryTotalPages(1);
  };

  const openProductHistoryModal = (productRow) => {
    const nextProductId = cleanText(productRow?.id);

    if (!extractPositiveNumber(nextProductId)) {
      errorNotify("Product id is not available");
      return;
    }

    setViewingProduct(productRow);
    setProductHistoryRows([]);
    setProductHistoryPage(1);
    setProductHistoryLimit(10);
    setProductHistoryTotalItems(0);
    setProductHistoryTotalPages(1);
    setProductHistoryModalOpen(true);
  };

  const openCreateCustomerRouteModal = () => {
    setCustomerRouteModalMode("create");
    setEditingCustomerRoute(null);
    setCustomerRouteForm(createCustomerRouteForm());
    setCustomerRouteErrors({});
    setCustomerRouteModalOpen(true);
  };

  const openEditCustomerRouteModal = (routeRow) => {
    const routeRecord = resolveCustomerRouteRecord(
      customerRoutes,
      routeRow?.customerRouteId || routeRow?.customer_route_id || routeRow?.id
    );

    const nextFormValues = {
      fromWarehouseId: firstFilled(
        resolveCustomerRouteWarehouseId(routeRecord, "from"),
        resolveCustomerRouteWarehouseId(routeRow, "from"),
        routeRow?.fromWarehouseId
      ),
      toWarehouseId: firstFilled(
        resolveCustomerRouteWarehouseId(routeRecord, "to"),
        resolveCustomerRouteWarehouseId(routeRow, "to"),
        routeRow?.toWarehouseId
      ),
      locationRate: firstFilled(
        routeRecord?.location_rate,
        routeRecord?.locationRate,
        routeRow?.locationRate,
        routeRow?.location_rate
      ),
      fixDistance: firstFilled(
        routeRecord?.fix_distance,
        routeRecord?.fixDistance,
        routeRow?.fixDistance
      ),
      changedAt: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
    };

    setCustomerRouteModalMode("edit");
    setEditingCustomerRoute({
      ...routeRow,
      routeRecord,
      customerRouteId:
        routeRow?.customerRouteId ||
        routeRow?.customer_route_id ||
        routeRecord?.id ||
        routeRecord?.customer_route_id,
    });
    setCustomerRouteForm(nextFormValues);
    setCustomerRouteErrors({});
    setIsChangedAtDirty(false);
    setCustomerRouteModalOpen(true);
  };

  const openCustomerRouteHistoryModal = (routeRow) => {
    const routeRecord = resolveCustomerRouteRecord(
      customerRoutes,
      routeRow?.customerRouteId || routeRow?.customer_route_id || routeRow?.id
    );

    const nextCustomerRouteId = cleanText(
      firstFilled(
        routeRow?.customerRouteId,
        routeRow?.customer_route_id,
        routeRecord?.id,
        routeRecord?.customer_route_id
      )
    );

    if (!extractPositiveNumber(nextCustomerRouteId)) {
      errorNotify("Customer route id is not available");
      return;
    }

    setViewingCustomerRoute({
      ...routeRow,
      routeRecord,
      customerRouteId: nextCustomerRouteId,
      fromWarehouseLabel:
        routeRow?.fromWarehouseLabel ||
        resolveCustomerRouteWarehouseLabel(routeRecord, {}, "from"),
      toWarehouseLabel:
        routeRow?.toWarehouseLabel ||
        resolveCustomerRouteWarehouseLabel(routeRecord, {}, "to"),
    });
    setCustomerRouteHistoryRows([]);
    setCustomerRouteHistoryPage(1);
    setCustomerRouteHistoryLimit(10);
    setCustomerRouteHistoryTotalItems(0);
    setCustomerRouteHistoryTotalPages(1);
    setCustomerRouteHistoryModalOpen(true);
  };

  const handleSubmitCustomerRoute = async () => {
    const nextErrors = validateCustomerRouteForm();
    setCustomerRouteErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      return;
    }

    setCustomerRouteSubmitting(true);

    try {
      if (customerRouteModalMode === "edit") {
        const finalChangedAt = isChangedAtDirty
          ? moment(customerRouteForm.changedAt).format("YYYY-MM-DD HH:mm:ss")
          : moment().format("YYYY-MM-DD HH:mm:ss");

        await apiService.updateCustomerRouteLocationRate({
          customer_route_id: Number(editingCustomerRoute?.customerRouteId),
          location_rate: Number(customerRouteForm.locationRate),
          changed_at: finalChangedAt,
        });
        successNotify("Customer route updated successfully");
      } else {
        const basePayload = {
          customer_id: resolvedCustomerEntityId,
          from_warehouse_id: Number(customerRouteForm.fromWarehouseId),
          to_warehouse_id: Number(customerRouteForm.toWarehouseId),
          location_rate: Number(customerRouteForm.locationRate),
          fix_distance: Number(customerRouteForm.fixDistance),
        };

        await apiService.createCustomerRoute(basePayload);
        successNotify("Customer route created successfully");
      }

      closeCustomerRouteModal();
      await loadCustomerRoutes(resolvedCustomerEntityId);
    } catch (error) {
      errorNotify(
        error ||
        (customerRouteModalMode === "edit"
          ? "Failed to update customer route"
          : "Failed to create customer route")
      );
    } finally {
      setCustomerRouteSubmitting(false);
    }
  };

  const handleSave = async (values, { setSubmitting }) => {
    try {
      if (isEditMode && !canUpdateCustomers) {
        errorNotify("You do not have permission to update customers.");
        return;
      }

      if (!isEditMode && !canCreateCustomers) {
        errorNotify("You do not have permission to create customers.");
        return;
      }

      const valuesWithCoordinates = await withResolvedWarehouseCoordinates(values);

      if (isEditMode) {
        const targetId = Number(resolvedCustomerEntityId || editRecordId || values.customerId);
        if (!Number.isFinite(targetId)) {
          errorNotify("Invalid customer id");
          return;
        }

        const payload = {
          id: targetId,
          ...toCreateCustomerPayload(valuesWithCoordinates, partUnitOptions),
        };
        await dispatch(updateCustomer(payload)).unwrap();
        successNotify("Customer updated successfully");
        navigate("/customers");
        return;
      }

      const payload = toCreateCustomerPayload(valuesWithCoordinates, partUnitOptions);
      await dispatch(createCustomer(payload)).unwrap();
      successNotify("Customer created successfully");
      navigate("/customers");
    } catch (error) {
      errorNotify(error || "Failed to save customer");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={isEditMode ? editCustomerSchema : stepSchemas[currentStep]}
      enableReinitialize
      onSubmit={handleSave}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        validateForm,
        setTouched,
        isSubmitting,
      }) => {
        const isLastStep = currentStep === stepItems.length - 1;
        const hasPrimaryContact = hasFilledContact(values.primaryContact);
        const hasSecondaryContacts = safeArray(values.secondaryContacts).length > 0;
        const deleteActionStyle = {
          border: "none",
          background: "transparent",
          color: "#EF0000",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: 0,
          fontSize: 15,
          fontWeight: 400,
          marginTop: 2,
        };
        const addActionBaseStyle = {
          width: "100%",
          borderRadius: 10,
          padding: "8px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          fontSize: 13,
          fontWeight: 400,
        };
        const getAddActionStyle = (disabled = false, extraStyles = {}) => ({
          ...addActionBaseStyle,
          border: disabled ? "1px solid #BDBDBD" : "1px solid #D3DEEE",
          background: disabled ? "#BDBDBD" : "#F4F7FD",
          color: disabled ? "#F2F2F2" : "#1447B1",
          cursor: disabled ? "not-allowed" : "pointer",
          ...extraStyles,
        });
        const sectionCardStyle = {
          background: "#FFFFFF",
          border: "1px solid #DDE3F0",
          borderRadius: 12,
          padding: "16px 14px",
          marginBottom: 14,
        };
        const sectionHeadingStyle = {
          background: "transparent",
          padding: 0,
          marginBottom: 12,
          color: "#3D3F5C",
          fontSize: 18,
          fontWeight: 600,
        };
        const requiredStar = (
          <span style={{ color: "red", fontSize: 13 }}>*</span>
        );
        const requiredLabel = (text, isRequired = true) =>
          isRequired ? (
            <>
              {text} {requiredStar}
            </>
          ) : (
            text
          );
        const detailTabs = [
          { key: "general", label: "General Detail" },
          { key: "routes", label: "Routes" },
        ];
        const isRoutesTabActive = isEditMode && activeDetailTab === "routes";
        const showGeneralDetailSections = !isEditMode || activeDetailTab === "general";
        const selectMenuPortalTarget =
          typeof document !== "undefined" ? document.body : null;
        const canManageRoutes =
          isEditMode && canUpdateCustomers && Boolean(resolvedCustomerEntityId);
        const routeWarehouseOptions = withMissingRouteLocationOptions(
          customerRouteLocationOptions,
          [editingCustomerRoute?.routeRecord, editingCustomerRoute].filter(Boolean)
        );
        const routeFromWarehouseOptions = routeWarehouseOptions.filter(
          (option) =>
            normalize(option?.value) === normalize(customerRouteForm.fromWarehouseId) ||
            normalize(option?.value) !== normalize(customerRouteForm.toWarehouseId)
        );
        const routeToWarehouseOptions = routeWarehouseOptions.filter(
          (option) =>
            normalize(option?.value) === normalize(customerRouteForm.toWarehouseId) ||
            normalize(option?.value) !== normalize(customerRouteForm.fromWarehouseId)
        );
        const routeModalSubmitDisabled =
          !canManageRoutes ||
          customerRouteSubmitting ||
          customerRouteLocationOptionsLoading ||
          routeWarehouseOptions.length < 2;
        const routeWarehouseLabelLookup = routeWarehouseOptions.reduce(
          (acc, option) => {
            acc[String(option.value)] = option.label;
            return acc;
          },
          {}
        );
        const routeRows = safeArray(customerRoutes).map((route, index) => ({
          id: route?.id || `route-${index}`,
          customerRouteId: cleanText(route?.id ?? route?.customer_route_id),
          fromWarehouseId: resolveCustomerRouteWarehouseId(route, "from"),
          toWarehouseId: resolveCustomerRouteWarehouseId(route, "to"),
          fromWarehouseLabel: resolveCustomerRouteWarehouseLabel(
            route,
            routeWarehouseLabelLookup,
            "from"
          ),
          toWarehouseLabel: resolveCustomerRouteWarehouseLabel(
            route,
            routeWarehouseLabelLookup,
            "to"
          ),
          locationRate: cleanText(route?.location_rate ?? route?.locationRate),
          fixDistance: cleanText(route?.fix_distance ?? route?.fixDistance),
          createdAt: route?.created_at ?? route?.createdAt,
          updatedAt: route?.updated_at ?? route?.updatedAt,
        }));
        const normalizedRouteSearchText = normalize(customerRouteLogsSearchText);
        const filteredRouteRows = routeRows.filter((row) => {
          if (!normalizedRouteSearchText) return true;

          return [
            row.fromWarehouseLabel,
            row.toWarehouseLabel,
            row.locationRate,
            row.fixDistance,
            formatDateTimeValue(row.createdAt),
            formatDateTimeValue(row.updatedAt),
          ].some((value) =>
            normalize(value).includes(normalizedRouteSearchText)
          );
        });
        const routeTotalItems = filteredRouteRows.length;
        const routeTotalPages = Math.max(
          1,
          Math.ceil(routeTotalItems / customerRouteLogsLimit)
        );
        const paginatedRouteRows = filteredRouteRows.slice(
          (customerRouteLogsPage - 1) * customerRouteLogsLimit,
          customerRouteLogsPage * customerRouteLogsLimit
        );
        const routeHistoryRows = safeArray(customerRouteHistoryRows).map(
          (historyRow, index) => {
            const changedByUser =
              historyRow?.user ||
              historyRow?.changed_by_user ||
              historyRow?.changedByUser ||
              {};
            const changedByName = firstFilled(
              resolveUserName(changedByUser),
              userNameLookup[cleanText(historyRow?.changed_by)],
              historyRow?.changed_by ? `User ${historyRow.changed_by}` : ""
            );

            const isInitialRate =
              !historyRow?.old_location_rate ||
              Number(historyRow?.old_location_rate) === 0;

            const initialCreatedAt =
              viewingCustomerRoute?.createdAt ||
              viewingCustomerRoute?.created_at ||
              viewingCustomerRoute?.routeRecord?.created_at ||
              viewingCustomerRoute?.routeRecord?.createdAt;

            const logDateTime = isInitialRate && initialCreatedAt
              ? initialCreatedAt
              : (historyRow?.changed_at ||
                historyRow?.changedAt ||
                historyRow?.updated_at ||
                historyRow?.updatedAt ||
                historyRow?.created_at ||
                historyRow?.createdAt);

            return {
              id: historyRow?.id || `route-history-${index}`,
              oldLocationRate: cleanText(historyRow?.old_location_rate),
              locationRate: cleanText(historyRow?.location_rate),
              changedAt: logDateTime,
              changedBy: changedByName || "--",
              changedByImage: resolveUserImage(changedByUser),
              current: Boolean(historyRow?.current),
            };
          }
        );
        const renderLocationCell = (label, id) => {
          if (!label && !id) return "--";
          if (label && id && String(label) !== String(id)) {
            return (
              <div className="d-inline-flex align-items-center gap-1 flex-nowrap">
                <span className="fw-medium text-dark">{label}</span>
                <span className="badge bg-light text-secondary border" title={`Location ID: ${id}`}>
                  ID: {id}
                </span>
              </div>
            );
          }
          return label || id;
        };

        const routeColumns = [
          {
            name: "Location ID",
            selector: (row) =>
              row.id ? (
                <span className="badge bg-light text-secondary border" title={`Location ID: ${row.id}`}>
                  ID: {row.id}
                </span>
              ) : (
                "--"
              ),
            sortable: false,
            minWidth: "140px",
          },
          {
            name: "From Location",
            selector: (row) => renderLocationCell(row.fromWarehouseLabel, row.fromWarehouseId),
            sortable: false,
            minWidth: "200px",
          },
          {
            name: "To Location",
            selector: (row) => renderLocationCell(row.toWarehouseLabel, row.toWarehouseId),
            sortable: false,
            minWidth: "200px",
          },
          {
            name: "Location Rate",
            selector: (row) => row.locationRate || "--",
            sortable: false,
            minWidth: "110px",
          },
          {
            name: "Fixed Distance",
            selector: (row) => row.fixDistance || "--",
            sortable: false,
            minWidth: "120px",
          },
          {
            name: "Created At",
            selector: (row) => formatDateTimeValue(row.createdAt),
            sortable: false,
            minWidth: "150px",
          },

          {
            name: "Action",
            selector: (row) => (
              <div className="table_action_btn">
                <button
                  type="button"
                  className="edit_btn"
                  title="View Rate Logs"
                  onClick={() => openCustomerRouteHistoryModal(row)}
                >
                  <FiEye />
                </button>
                {canManageRoutes ? (
                  <button
                    type="button"
                    className="edit_btn"
                    title="Edit"
                    onClick={() => openEditCustomerRouteModal(row)}
                  >
                    <PiPencilSimpleLine />
                  </button>
                ) : (
                  !extractPositiveNumber(row?.customerRouteId) && "--"
                )}
              </div>
            ),
            sortable: false,
            minWidth: "90px",
            grow: 0,
          },
        ];
        const routeHistoryColumns = [
          {
            name: "Old Rate",
            selector: (row) => row.oldLocationRate || "--",
            sortable: false,
            minWidth: "100px",
          },
          {
            name: "Updated Rate",
            selector: (row) => row.locationRate || "--",
            sortable: false,
            minWidth: "110px",
          },
          {
            name: "Changed At",
            selector: (row) => {
              if (!row.changedAt) return "--";
              const m = moment.utc(row.changedAt);
              return m.isValid() ? m.format("DD-MMM-YYYY hh:mm A") : "--";
            },
            sortable: false,
            minWidth: "150px",
          },
          {
            name: "Changed By",
            selector: (row) =>
              row.changedBy && row.changedBy !== "--" ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <UserAvatar
                    username={row.changedBy}
                    image={row.changedByImage}
                    marginRight="0px"
                    size={28}
                  />
                  <span className="hyperlink">{row.changedBy}</span>
                </div>
              ) : (
                "--"
              ),
            sortable: false,
            minWidth: "180px",
          },
          {
            name: "Current",
            selector: (row) => (row.current ? "Yes" : "No"),
            sortable: false,
            minWidth: "80px",
            maxWidth: "90px",
          },
        ];

        const productHistoryRowsMapped = safeArray(productHistoryRows).map(
          (historyRow, index) => {
            const changedByUser =
              historyRow?.user ||
              historyRow?.changed_by_user ||
              historyRow?.changedByUser ||
              {};
            const changedByName = firstFilled(
              resolveUserName(changedByUser),
              userNameLookup[cleanText(historyRow?.changed_by)],
              historyRow?.changed_by ? `User ${historyRow.changed_by}` : ""
            );

            const isInitialRate =
              !historyRow?.old_price ||
              Number(historyRow?.old_price) === 0;

            const initialCreatedAt =
              viewingProduct?.createdAt ||
              viewingProduct?.created_at;

            const logDateTime = isInitialRate && initialCreatedAt
              ? initialCreatedAt
              : (historyRow?.changed_at ||
                historyRow?.changedAt ||
                historyRow?.updated_at ||
                historyRow?.updatedAt ||
                historyRow?.created_at ||
                historyRow?.createdAt);

            return {
              id: historyRow?.id || `product-history-${index}`,
              oldPrice: cleanText(historyRow?.old_price),
              price: cleanText(historyRow?.price),
              changedAt: logDateTime,
              changedBy: changedByName || "--",
              changedByImage: resolveUserImage(changedByUser),
              current: Boolean(historyRow?.current),
            };
          }
        );

        const productHistoryColumns = [
          {
            name: "Old Rate",
            selector: (row) => row.oldPrice || "--",
            sortable: false,
            minWidth: "100px",
          },
          {
            name: "Updated Rate",
            selector: (row) => row.price || "--",
            sortable: false,
            minWidth: "110px",
          },
          {
            name: "Changed At",
            selector: (row) => formatDateValue(row.changedAt),
            sortable: false,
            minWidth: "150px",
          },
          {
            name: "Changed By",
            selector: (row) =>
              row.changedBy && row.changedBy !== "--" ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <UserAvatar
                    username={row.changedBy}
                    image={row.changedByImage}
                    marginRight="0px"
                    size={28}
                  />
                  <span className="hyperlink">{row.changedBy}</span>
                </div>
              ) : (
                "--"
              ),
            sortable: false,
            minWidth: "180px",
          },
          {
            name: "Current",
            selector: (row) => (row.current ? "Yes" : "No"),
            sortable: false,
            minWidth: "80px",
            maxWidth: "90px",
          },
        ];

        const nextStep = async () => {
          const allErrors = await validateForm();
          if (Object.keys(allErrors || {}).length) {
            setTouched(touchedFromErrors(allErrors));
            window.setTimeout(() => scrollToFirstError(allErrors), 120);
            return;
          }
          setCurrentStep((prev) => Math.min(prev + 1, stepItems.length - 1));
        };

        const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

        const gotoStep = async (target) => {
          if (target <= currentStep) {
            setCurrentStep(target);
            return;
          }

          const allErrors = await validateForm();
          if (Object.keys(allErrors || {}).length) {
            setTouched(touchedFromErrors(allErrors));
            window.setTimeout(() => scrollToFirstError(allErrors), 120);
            return;
          }
          setCurrentStep(target);
        };

        const submitCustomerForm = async () => {
          const allErrors = await validateForm();
          if (Object.keys(allErrors || {}).length) {
            setTouched(touchedFromErrors(allErrors));
            window.setTimeout(() => scrollToFirstError(allErrors), 120);
            return;
          }

          handleSubmit();
        };

        const addSecondary = () => {
          setFieldValue("secondaryContacts", [
            ...safeArray(values.secondaryContacts),
            createSecondary(),
          ]);
        };

        const removeSecondary = (id) => {
          const nextItems = safeArray(values.secondaryContacts).filter(
            (item) => item.id !== id
          );
          setFieldValue("secondaryContacts", nextItems);
        };

        const addWarehouseRow = () => {
          setFieldValue("warehouses", [...safeArray(values.warehouses), createWarehouse()]);
        };

        const removeWarehouseRow = (id) => {
          const nextItems = safeArray(values.warehouses).filter(
            (item) => item.id !== id
          );
          if (nextItems.length < minimumWarehouseCount) return;
          setFieldValue("warehouses", nextItems);
        };

        const resetLocationTableForm = () => {
          setShowLocationForm(false);
          setEditingLocationId(null);
          setLocationDraft(createCustomerLocationDraft());
          setLocationDraftErrors({});
        };

        const openCreateLocationTableForm = () => {
          setEditingLocationId(null);
          setLocationDraft(createCustomerLocationDraft());
          setLocationDraftErrors({});
          setShowLocationForm(true);
        };

        const openEditLocationTableForm = (item) => {
          setEditingLocationId(item?.id);
          setLocationDraft({
            ...createCustomerLocationDraft(),
            ...item,
            id: item?.id || createTempId(),
            status: resolveWarehouseStatus(
              item?.status ?? item?.is_active ?? item?.status_id
            ),
          });
          setLocationDraftErrors({});
          setShowLocationForm(true);
        };

        const handleLocationDraftChange = (name, value) => {
          setLocationDraftErrors((prev) => {
            if (prev[name]) {
              const next = { ...prev };
              delete next[name];
              return next;
            }
            return prev;
          });
          setLocationDraft((previous) => ({
            ...previous,
            [name]: value,
          }));
        };

        const saveLocationTableRow = async () => {
          try {
            await customerLocationTableSchema.validate(locationDraft, {
              abortEarly: false,
            });

            setLocationSubmitting(true);

            let latitude = cleanText(locationDraft.warehouseLatitude);
            let longitude = cleanText(locationDraft.warehouseLongitude);
            if (cleanText(locationDraft.warehouseAddress) && (!latitude || !longitude)) {
              try {
                const coordinates = await resolveAddressCoordinates(locationDraft.warehouseAddress);
                latitude = coordinates.lat;
                longitude = coordinates.lng;
              } catch (e) {
                // Ignore coordinates resolution error
              }
            }

            const payload = {
              customer_id: Number(resolvedCustomerEntityId),
              warehouse_name: cleanText(locationDraft.warehouseName),
              city: locationDraft.city,
              address: cleanText(locationDraft.warehouseAddress),
              contact: cleanText(locationDraft.contact),
              contact_person_name: cleanText(locationDraft.contactPersonName),
              contact_person_number: cleanText(locationDraft.contactPersonNumber),
              lat: String(latitude || ""),
              lng: String(longitude || ""),
              province: normalizeProvinceValue(locationDraft.province),
              status: cleanText(locationDraft.status).toLowerCase(),
            };

            if (editingLocationId) {
              payload.customer_warehouses_id = Number(editingLocationId);
              payload.id = Number(editingLocationId);
              await apiService.updateCustomerWarehouse(payload);

              const nextLocation = {
                ...locationDraft,
                warehouseLatitude: latitude,
                warehouseLongitude: longitude,
                id: editingLocationId,
              };
              const currentLocations = safeArray(values.warehouses);
              const nextLocations = currentLocations.map((item) =>
                String(item?.id) === String(editingLocationId)
                  ? nextLocation
                  : item
              );

              setFieldValue("warehouses", nextLocations);
              successNotify("Warehouse location updated successfully");
            } else {
              const res = await apiService.createCustomerWarehouse(payload);
              const createdId = res?.data?.id || res?.id || createTempId();

              const nextLocation = {
                ...locationDraft,
                warehouseLatitude: latitude,
                warehouseLongitude: longitude,
                id: createdId,
              };
              const currentLocations = safeArray(values.warehouses);
              setFieldValue("warehouses", [...currentLocations, nextLocation]);
              successNotify("Warehouse location created successfully");
            }

            setFieldTouched("warehouses", true, false);
            resetLocationTableForm();
          } catch (error) {
            if (error?.inner) {
              const nextErrors = {};
              safeArray(error?.inner).forEach((item) => {
                if (item?.path && !nextErrors[item.path]) {
                  nextErrors[item.path] = item.message;
                }
              });
              setLocationDraftErrors(nextErrors);
            } else {
              errorNotify(error || "Something went wrong");
            }
          } finally {
            setLocationSubmitting(false);
          }
        };

        const openLocationDeleteModal = (item) => {
          const currentLocations = safeArray(values.warehouses);

          if (currentLocations.length <= minimumWarehouseCount) {
            errorNotify(
              `At least ${minimumWarehouseCount} locations are required`
            );
            return;
          }

          setLocationDeleteTarget(item);
          setLocationDeleteModalOpen(true);
        };

        const closeLocationDeleteModal = () => {
          setLocationDeleteModalOpen(false);
          setLocationDeleteTarget(null);
        };

        const confirmDeleteLocationTableRow = async () => {
          const id = locationDeleteTarget?.id;
          const currentLocations = safeArray(values.warehouses);

          if (!id) {
            closeLocationDeleteModal();
            return;
          }

          if (currentLocations.length <= minimumWarehouseCount) {
            errorNotify(
              `At least ${minimumWarehouseCount} locations are required`
            );
            closeLocationDeleteModal();
            return;
          }

          setLocationSubmitting(true);
          try {
            if (Number.isFinite(Number(id))) {
              await apiService.deleteCustomerWarehouse(id);
            }

            const nextLocations = currentLocations.filter(
              (item) => String(item?.id) !== String(id)
            );
            setFieldValue("warehouses", nextLocations);
            setFieldTouched("warehouses", true, false);

            if (String(editingLocationId) === String(id)) {
              resetLocationTableForm();
            }
            successNotify("Warehouse location deleted successfully");
          } catch (error) {
            errorNotify(error || "Failed to delete warehouse location");
          } finally {
            setLocationSubmitting(false);
            closeLocationDeleteModal();
          }
        };

        const resetProductTableForm = () => {
          setShowProductForm(false);
          setEditingProductId(null);
          setProductDraft(createProduct());
          setProductDraftErrors({});
        };

        const openCreateProductTableForm = () => {
          setEditingProductId(null);
          setProductDraft(createProduct());
          setProductDraftErrors({});
          setShowProductForm(true);
        };

        const openEditProductTableForm = (item) => {
          setEditingProductId(item?.id);
          setProductDraft({
            ...createProduct(),
            ...item,
            id: item?.id || createTempId(),
          });
          setProductDraftErrors({});
          setShowProductForm(true);
        };

        const handleProductDraftChange = (name, value) => {
          setProductDraftErrors((prev) => {
            if (prev[name]) {
              const next = { ...prev };
              delete next[name];
              return next;
            }
            return prev;
          });

          if (name === "insuranceCoverValue") {
            if (value === "") {
              setProductDraft((previous) => ({
                ...previous,
                [name]: "",
              }));
              return;
            }
            if (/^\d*\.?\d*$/.test(value)) {
              const num = parseFloat(value);
              if (isNaN(num) || num <= 100) {
                setProductDraft((previous) => ({
                  ...previous,
                  [name]: value,
                }));
              }
            }
            return;
          }
          setProductDraft((previous) => ({
            ...previous,
            [name]: value,
          }));
        };

        const saveProductTableRow = async () => {
          try {
            await customerProductTableSchema.validate(productDraft, {
              abortEarly: false,
            });

            setProductSubmitting(true);
            if (editingProductId) {
              const payload = {
                id: Number(editingProductId),
                customer_id: Number(resolvedCustomerEntityId),
                name: productDraft.name,
                unit: productDraft.unit,
                product_type: productDraft.productType,
                insurance_cover_type: productDraft.insuranceCoverType,
                insurance_status: productDraft.insuranceStatus,
                insurance_cover_value: Number(productDraft.insuranceCoverValue),
              };
              await apiService.updateProduct(payload);

              const nextProduct = {
                ...productDraft,
                id: editingProductId,
              };
              const currentProducts = safeArray(values.products);
              const nextProducts = currentProducts.map((item) =>
                String(item?.id) === String(editingProductId) ? nextProduct : item
              );
              setFieldValue("products", nextProducts);
              successNotify("Product updated successfully");
            } else {
              const payload = {
                customer_id: Number(resolvedCustomerEntityId),
                name: productDraft.name,
                unit: productDraft.unit,
                product_type: productDraft.productType,
                product_value: Number(productDraft.productValue),
                insurance_cover_type: productDraft.insuranceCoverType,
                insurance_status: productDraft.insuranceStatus,
                insurance_cover_value: Number(productDraft.insuranceCoverValue),
              };
              const res = await apiService.createProduct(payload);
              const createdId = res?.data?.id || res?.id || createTempId();

              const nextProduct = {
                ...productDraft,
                id: createdId,
              };
              const currentProducts = safeArray(values.products);
              setFieldValue("products", [...currentProducts, nextProduct]);
              successNotify("Product created successfully");
            }

            setFieldTouched("products", true, false);
            resetProductTableForm();
          } catch (error) {
            if (error?.inner) {
              const nextErrors = {};
              safeArray(error?.inner).forEach((item) => {
                if (item?.path && !nextErrors[item.path]) {
                  nextErrors[item.path] = item.message;
                }
              });
              setProductDraftErrors(nextErrors);
            } else {
              errorNotify(error || "Something went wrong");
            }
          } finally {
            setProductSubmitting(false);
          }
        };

        const openProductDeleteModal = (item) => {
          const currentProducts = safeArray(values.products);

          if (currentProducts.length <= minimumProductCount) {
            errorNotify(
              `At least ${minimumProductCount} product is required`
            );
            return;
          }

          setProductDeleteTarget(item);
          setProductDeleteModalOpen(true);
        };

        const closeProductDeleteModal = () => {
          setProductDeleteModalOpen(false);
          setProductDeleteTarget(null);
        };

        const confirmDeleteProductTableRow = async () => {
          const id = productDeleteTarget?.id;
          const currentProducts = safeArray(values.products);

          if (!id) {
            closeProductDeleteModal();
            return;
          }

          if (currentProducts.length <= minimumProductCount) {
            errorNotify(
              `At least ${minimumProductCount} product is required`
            );
            closeProductDeleteModal();
            return;
          }

          setProductSubmitting(true);
          try {
            if (typeof id === "number" || !isNaN(Number(id))) {
              await apiService.deleteProduct(id);
            }

            const nextProducts = currentProducts.filter(
              (item) => String(item?.id) !== String(id)
            );
            setFieldValue("products", nextProducts);
            setFieldTouched("products", true, false);

            if (String(editingProductId) === String(id)) {
              resetProductTableForm();
            }

            successNotify("Product deleted successfully");
            closeProductDeleteModal();
          } catch (error) {
            errorNotify(error || "Failed to delete product");
          } finally {
            setProductSubmitting(false);
          }
        };

        const resetSecondaryTableForm = () => {
          setShowSecondaryForm(false);
          setEditingSecondaryId(null);
          setSecondaryDraft(createSecondary());
          setSecondaryDraftErrors({});
        };

        const openCreateSecondaryTableForm = () => {
          setEditingSecondaryId(null);
          setSecondaryDraft(createSecondary());
          setSecondaryDraftErrors({});
          setShowSecondaryForm(true);
        };

        const openEditSecondaryTableForm = (item, isPrimary = false) => {
          if (isPrimary) {
            setEditingSecondaryId("primary");
            setSecondaryDraft({
              ...createSecondary(),
              ...item,
              id: "primary",
            });
          } else {
            setEditingSecondaryId(item?.id);
            setSecondaryDraft({
              ...createSecondary(),
              ...item,
              id: item?.id || createTempId(),
            });
          }
          setSecondaryDraftErrors({});
          setShowSecondaryForm(true);
        };

        const handleSecondaryDraftChange = (name, value) => {
          setSecondaryDraftErrors((prev) => {
            if (prev[name]) {
              const next = { ...prev };
              delete next[name];
              return next;
            }
            return prev;
          });
          setSecondaryDraft((previous) => ({
            ...previous,
            [name]: value,
          }));
        };

        const saveSecondaryTableRow = async () => {
          try {
            const shouldSaveAsPrimary =
              editingSecondaryId === "primary" || !hasPrimaryContact;
            const validationSchema = shouldSaveAsPrimary
              ? Yup.object().shape({
                name: Yup.string().trim().required("Primary contact name is required"),
                contact: buildPhoneNumberSchema("Primary contact number is required"),
                designation: Yup.string().required("Primary designation is required"),
                email: Yup.string()
                  .trim()
                  .required("Primary email is required")
                  .email("Invalid email address"),
              })
              : customerSecondaryContactTableSchema;

            await validationSchema.validate(secondaryDraft, {
              abortEarly: false,
            });

            if (shouldSaveAsPrimary) {
              setFieldValue("primaryContact", {
                name: secondaryDraft.name,
                contact: secondaryDraft.contact,
                designation: secondaryDraft.designation,
                email: secondaryDraft.email,
              });
              setFieldTouched("primaryContact", true, false);
            } else {
              const nextSecondary = {
                ...secondaryDraft,
                id: editingSecondaryId || secondaryDraft?.id || createTempId(),
              };
              const currentSecondaries = safeArray(values.secondaryContacts);
              const nextSecondaries = editingSecondaryId
                ? currentSecondaries.map((item) =>
                  String(item?.id) === String(editingSecondaryId)
                    ? nextSecondary
                    : item
                )
                : [...currentSecondaries, nextSecondary];

              setFieldValue("secondaryContacts", nextSecondaries);
              setFieldTouched("secondaryContacts", true, false);
            }
            resetSecondaryTableForm();
          } catch (error) {
            const nextErrors = {};
            safeArray(error?.inner).forEach((item) => {
              if (item?.path && !nextErrors[item.path]) {
                nextErrors[item.path] = item.message;
              }
            });
            setSecondaryDraftErrors(nextErrors);
          }
        };

        const openSecondaryDeleteModal = (item) => {
          setSecondaryDeleteTarget(item);
          setSecondaryDeleteModalOpen(true);
        };

        const closeSecondaryDeleteModal = () => {
          setSecondaryDeleteModalOpen(false);
          setSecondaryDeleteTarget(null);
        };

        const confirmDeleteSecondaryTableRow = () => {
          const id = secondaryDeleteTarget?.id;
          const currentSecondaries = safeArray(values.secondaryContacts);

          if (!id) {
            closeSecondaryDeleteModal();
            return;
          }

          const nextSecondaries = currentSecondaries.filter(
            (item) => String(item?.id) !== String(id)
          );
          setFieldValue("secondaryContacts", nextSecondaries);
          setFieldTouched("secondaryContacts", true, false);

          if (String(editingSecondaryId) === String(id)) {
            resetSecondaryTableForm();
          }

          closeSecondaryDeleteModal();
        };

        const addProductRow = () => {
          setFieldValue("products", [...safeArray(values.products), createProduct()]);
        };

        const removeProductRow = (id) => {
          const nextItems = safeArray(values.products).filter(
            (item) => item.id !== id
          );
          if (nextItems.length < minimumProductCount) return;
          setFieldValue("products", nextItems);
        };

        const isEditingLocationTableRow =
          editingLocationId !== null && editingLocationId !== undefined;
        const isEditingProductTableRow =
          editingProductId !== null && editingProductId !== undefined;

        const renderLocationTableForm = (inline = false) => (
          <div className={inline ? "service_line_form" : "service_line_form mt-3"}>
            <div>
              <div className="service_line_item_header">
                <h5>
                  {isEditingLocationTableRow
                    ? "Update Location"
                    : "Add Location"}
                </h5>
                <div
                  className="save_button_container d-flex align-items-center"
                  style={{ gap: 8 }}
                >
                  <button
                    type="button"
                    className="main_btn"
                    onClick={saveLocationTableRow}
                    disabled={locationSubmitting}
                  >
                    {locationSubmitting ? "Submitting..." : (isEditingLocationTableRow ? "Update" : "Save")}
                  </button>
                  <button
                    type="button"
                    className="cancel_btn"
                    onClick={resetLocationTableForm}
                    disabled={locationSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
              <Row>
                <Col md={6}>
                  <Input
                    label="Location Name"
                    placeholder="Enter Location"
                    name="warehouseName"
                    value={locationDraft.warehouseName}
                    onChange={(event) =>
                      handleLocationDraftChange(
                        "warehouseName",
                        event.target.value
                      )
                    }
                    onBlur={() => { }}
                    error={locationDraftErrors.warehouseName}
                    touched={Boolean(
                      locationDraftErrors.warehouseName
                    )}
                  />
                </Col>
                <Col md={6}>
                  <Select
                    label={requiredLabel("City")}
                    placeholder="Select City"
                    options={warehouseCityOptions}
                    loading={dropdownLoading}
                    value={pickOption(
                      warehouseCityOptions,
                      locationDraft.city
                    )}
                    name="city"
                    onChange={(opt) =>
                      handleLocationDraftChange(
                        "city",
                        opt ? opt.value : ""
                      )
                    }
                    onBlur={() => { }}
                    error={locationDraftErrors.city}
                    touched={Boolean(
                      locationDraftErrors.city
                    )}
                  />
                </Col>
                <Col md={6}>
                  <PhoneNumberInput
                    label="Contact"
                    name="contact"
                    value={locationDraft.contact}
                    setFieldValue={(_, value) =>
                      handleLocationDraftChange(
                        "contact",
                        value
                      )
                    }
                    onBlur={() => { }}
                    error={locationDraftErrors.contact}
                    touched={Boolean(
                      locationDraftErrors.contact
                    )}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    label="Contact Person Name"
                    placeholder="Enter Contact Person Name"
                    name="contactPersonName"
                    value={locationDraft.contactPersonName}
                    onChange={(event) =>
                      handleLocationDraftChange(
                        "contactPersonName",
                        event.target.value
                      )
                    }
                    onBlur={() => { }}
                    error={
                      locationDraftErrors.contactPersonName
                    }
                    touched={Boolean(
                      locationDraftErrors.contactPersonName
                    )}
                  />
                </Col>
                <Col md={6}>
                  <PhoneNumberInput
                    label="Contact Person Number"
                    name="contactPersonNumber"
                    value={
                      locationDraft.contactPersonNumber
                    }
                    setFieldValue={(_, value) =>
                      handleLocationDraftChange(
                        "contactPersonNumber",
                        value
                      )
                    }
                    onBlur={() => { }}
                    error={
                      locationDraftErrors.contactPersonNumber
                    }
                    touched={Boolean(
                      locationDraftErrors.contactPersonNumber
                    )}
                  />
                </Col>
                <Col md={6}>
                  <Select
                    label={requiredLabel("Status")}
                    placeholder="Select Status"
                    options={locationStatusOptions}
                    value={
                      locationStatusOptions.find(
                        (opt) =>
                          opt.value ===
                          resolveWarehouseStatus(locationDraft.status)
                      ) || null
                    }
                    name="status"
                    onChange={(opt) =>
                      handleLocationDraftChange(
                        "status",
                        opt ? opt.value : ""
                      )
                    }
                    onBlur={() => { }}
                    error={locationDraftErrors.status}
                    touched={Boolean(locationDraftErrors.status)}
                  />
                </Col>
                <Col md={12}>
                  <GoogleMapInput
                    label="Address"
                    placeholder="Search address..."
                    name="warehouseAddress"
                    value={locationDraft.warehouseAddress}
                    setFieldValue={(fieldName, value) =>
                      handleLocationDraftChange(
                        fieldName,
                        value
                      )
                    }
                    onChange={(
                      address,
                      latitude,
                      longitude,
                      rawResult
                    ) => {
                      handleLocationDraftChange(
                        "warehouseAddress",
                        address || ""
                      );
                      handleLocationDraftChange(
                        "warehouseLatitude",
                        latitude || ""
                      );
                      handleLocationDraftChange(
                        "warehouseLongitude",
                        longitude || ""
                      );

                      const resolvedProv = detectProvince(address, rawResult);
                      if (resolvedProv) {
                        handleLocationDraftChange(
                          "province",
                          resolvedProv
                        );
                      }
                    }}
                    error={
                      locationDraftErrors.warehouseAddress
                    }
                    touched={Boolean(
                      locationDraftErrors.warehouseAddress
                    )}
                    storeAs="string"
                    latitudeFieldName="warehouseLatitude"
                    longitudeFieldName="warehouseLongitude"
                  />
                </Col>
              </Row>
            </div>
          </div>
        );

        const renderProductTableForm = (inline = false) => (
          <div className={inline ? "service_line_form" : "service_line_form mt-3"}>
            <div>
              <div className="service_line_item_header">
                <h5>
                  {isEditingProductTableRow
                    ? "Update Product"
                    : "Add Product"}
                </h5>
                <div
                  className="save_button_container d-flex align-items-center"
                  style={{ gap: 8 }}
                >
                  <button
                    type="button"
                    className="main_btn"
                    onClick={saveProductTableRow}
                    disabled={productSubmitting}
                  >
                    {productSubmitting ? "Submitting..." : (isEditingProductTableRow ? "Update" : "Save")}
                  </button>
                  <button
                    type="button"
                    className="cancel_btn"
                    onClick={resetProductTableForm}
                    disabled={productSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
              <Row>
                <Col md={6}>
                  <Input
                    label="Product Name"
                    placeholder="Enter Product Name"
                    name="name"
                    value={productDraft.name}
                    onChange={(event) =>
                      handleProductDraftChange(
                        "name",
                        event.target.value
                      )
                    }
                    onBlur={() => { }}
                    error={productDraftErrors.name}
                    touched={Boolean(
                      productDraftErrors.name
                    )}
                  />
                </Col>
                <Col md={6}>
                  <Select
                    label={requiredLabel("Unit")}
                    placeholder="Select Unit"
                    options={partUnitOptions}
                    loading={dropdownLoading}
                    value={pickOption(
                      partUnitOptions,
                      productDraft.unit
                    )}
                    name="unit"
                    onChange={(opt) =>
                      handleProductDraftChange(
                        "unit",
                        opt ? opt.value : ""
                      )
                    }
                    onBlur={() => { }}
                    error={productDraftErrors.unit}
                    touched={Boolean(
                      productDraftErrors.unit
                    )}
                  />
                </Col>
                <Col md={6}>
                  <Select
                    label={requiredLabel("Product Type")}
                    placeholder="Select Product Type"
                    options={productTypeOptions}
                    value={pickOption(
                      productTypeOptions,
                      productDraft.productType
                    )}
                    name="productType"
                    onChange={(opt) =>
                      handleProductDraftChange(
                        "productType",
                        opt ? opt.value : ""
                      )
                    }
                    onBlur={() => { }}
                    error={productDraftErrors.productType}
                    touched={Boolean(
                      productDraftErrors.productType
                    )}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    label="Product Value"
                    type="number"
                    placeholder="Enter Product Value"
                    name="productValue"
                    value={productDraft.productValue}
                    onChange={(event) =>
                      handleProductDraftChange(
                        "productValue",
                        event.target.value
                      )
                    }
                    onBlur={() => { }}
                    disabled={isEditingProductTableRow}
                    error={productDraftErrors.productValue}
                    touched={Boolean(
                      productDraftErrors.productValue
                    )}
                  />
                </Col>
                <Col md={6}>
                  <Select
                    label={requiredLabel("Insurance Cover Type")}
                    placeholder="Select Insurance Cover Type"
                    options={insuranceCoverTypeOptions}
                    value={pickOption(
                      insuranceCoverTypeOptions,
                      productDraft.insuranceCoverType
                    )}
                    name="insuranceCoverType"
                    onChange={(opt) =>
                      handleProductDraftChange(
                        "insuranceCoverType",
                        opt ? opt.value : ""
                      )
                    }
                    onBlur={() => { }}
                    error={productDraftErrors.insuranceCoverType}
                    touched={Boolean(
                      productDraftErrors.insuranceCoverType
                    )}
                  />
                </Col>
                <Col md={6}>
                  <Select
                    label={requiredLabel("Insurance Status")}
                    placeholder="Select Insurance Status"
                    options={insuranceStatusOptions}
                    value={pickOption(
                      insuranceStatusOptions,
                      productDraft.insuranceStatus
                    )}
                    name="insuranceStatus"
                    onChange={(opt) =>
                      handleProductDraftChange(
                        "insuranceStatus",
                        opt ? opt.value : ""
                      )
                    }
                    onBlur={() => { }}
                    error={productDraftErrors.insuranceStatus}
                    touched={Boolean(
                      productDraftErrors.insuranceStatus
                    )}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    label="Insurance Cover Value"
                    type="text"
                    placeholder="Enter Insurance Cover Value"
                    name="insuranceCoverValue"
                    value={productDraft.insuranceCoverValue}
                    onChange={(event) =>
                      handleProductDraftChange(
                        "insuranceCoverValue",
                        event.target.value
                      )
                    }
                    onBlur={() => { }}
                    error={productDraftErrors.insuranceCoverValue}
                    touched={Boolean(
                      productDraftErrors.insuranceCoverValue
                    )}
                  />
                </Col>
              </Row>
            </div>
          </div>
        );

        return (
          <>
            <Breadcrumbs
              values={[
                "Customers",
                isEditMode ? "Detail Customer" : "Add Customer",
              ]}
            />

            <div className="page_header access_view">
              <h2>{isEditMode ? "Detail Customer" : "Add Customer"}</h2>
              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate("/customers")}
                  shouldRow={false}
                  isShowSubmitBtn={false}
                  cancelBtnText="Cancel"
                />
                <Button
                  submitText="Save"
                  submitBtn={submitCustomerForm}
                  type="button"
                  loading={isSubmitting || createCustomerLoading || updateCustomerLoading}
                  disabled={
                    !canSubmitChanges ||
                    (isEditMode && activeDetailTab !== "general") ||
                    (!isEditMode && !isLastStep) ||
                    isSubmitting ||
                    createCustomerLoading ||
                    updateCustomerLoading
                  }
                  shouldRow={false}
                  isShowCancel={false}
                  isShowSubmitBtn={canSubmitChanges && (!isEditMode || activeDetailTab === "general")}
                />
              </div>
            </div>

            <div className="page_container">
              <Row>
                <Col md={isRoutesTabActive ? 12 : 9}>
                  <div className="trip_information_main_div">
                    <h5>Customer Details</h5>
                    <div>
                      <h6>
                        Customer ID: <span>{values.customerId || "--"}</span>
                      </h6>
                      {isEditMode && (
                        <CustomTabs
                          tabs={detailTabs}
                          activeTab={activeDetailTab}
                          setActiveTab={setActiveDetailTab}
                        />
                      )}
                      <div className="d-flex">
                        {!isEditMode && (
                          <div style={{ minWidth: "175px" }}>
                            <Steps
                              className="transporter_steps form_steps_vertical"
                              current={currentStep}
                              direction="vertical"
                              items={stepItems}
                              onChange={gotoStep}
                            />
                          </div>
                        )}
                        <div className="w-100 mb-3">
                          {showGeneralDetailSections && (isEditMode || currentStep === 0) && (
                            <div style={isEditMode ? sectionCardStyle : undefined}>
                              {isEditMode && (
                                <h6 style={sectionHeadingStyle}>General Details</h6>
                              )}
                              <Row>
                                <Col md={6}>
                                  <Input
                                    label="Customer Name"
                                    placeholder="Enter Name"
                                    name="customerName"
                                    value={values.customerName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.customerName}
                                    touched={touched.customerName}
                                  />
                                </Col>
                                <Col md={6}>
                                  <PhoneNumberInput
                                    label="Contact"
                                    name="contact"
                                    value={values.contact}
                                    setFieldValue={setFieldValue}
                                    onBlur={handleBlur}
                                    error={errors.contact}
                                    touched={touched.contact}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label="Email Address"
                                    placeholder="Enter Email"
                                    name="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.email}
                                    touched={touched.email}
                                    required
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Cost Center")}
                                    placeholder="Select Cost Center"
                                    options={costCenterDropdownData}
                                    value={costCenterDropdownData?.find((o) => String(o.value) === String(values.costCenterId)) || null}
                                    name="costCenterId"
                                    onChange={(opt) =>
                                      setFieldValue("costCenterId", opt ? opt.value : "")
                                    }
                                    onBlur={() => setFieldTouched("costCenterId", true)}
                                    error={errors.costCenterId}
                                    touched={touched.costCenterId}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Industry Type")}
                                    placeholder="Select Industry"
                                    options={industryTypeOptions}
                                    value={pickOption(industryTypeOptions, values.industryType)}
                                    name="industryType"
                                    onChange={(opt) =>
                                      setFieldValue("industryType", opt ? opt.value : "")
                                    }
                                    onBlur={() => setFieldTouched("industryType", true)}
                                    error={errors.industryType}
                                    touched={touched.industryType}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Select
                                    label={requiredLabel("Status")}
                                    placeholder="Select Status"
                                    options={statusOptions}
                                    value={pickOption(statusOptions, values.status)}
                                    name="status"
                                    onChange={(opt) =>
                                      setFieldValue("status", opt ? opt.value : "")
                                    }
                                    onBlur={() => setFieldTouched("status", true)}
                                    error={errors.status}
                                    touched={touched.status}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label={requiredLabel("NTN Number")}
                                    placeholder="1234567-8"
                                    name="ntn_number"
                                    value={values.ntn_number}
                                    onChange={(e) => {
                                      const val = e.target.value.replace(/[^0-9-]/g, "");
                                      setFieldValue("ntn_number", val);
                                    }}
                                    onBlur={handleBlur}
                                    error={errors.ntn_number}
                                    touched={touched.ntn_number}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label={requiredLabel("STRN Number")}
                                    placeholder="12-34-5678-901-23"
                                    name="sntn_number"
                                    value={values.sntn_number}
                                    onChange={(e) => {
                                      const val = e.target.value.replace(/[^0-9-]/g, "");
                                      setFieldValue("sntn_number", val);
                                    }}
                                    onBlur={handleBlur}
                                    error={errors.sntn_number}
                                    touched={touched.sntn_number}
                                  />
                                </Col>
                                <Col md={12}>
                                  <Input
                                    label={requiredLabel("Billing Address")}
                                    placeholder="Enter Registered / Billing Address"
                                    name="address"
                                    value={values.address}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.address}
                                    touched={touched.address}
                                  />
                                </Col>
                                {!isEditMode && (
                                  <Col md={12}>
                                    <div className="d-flex align-items-center gap-2">
                                      <button type="button" className="cancel_btn" disabled>
                                        Previous
                                      </button>
                                      <button
                                        type="button"
                                        className="main_btn"
                                        onClick={nextStep}
                                      >
                                        Next
                                      </button>
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            </div>
                          )}

                          {showGeneralDetailSections && (isEditMode || currentStep === 1) && (
                            <div style={isEditMode ? sectionCardStyle : undefined}>
                              {isEditMode && (
                                <h6 style={sectionHeadingStyle}>Point of Contact</h6>
                              )}
                              {!isEditMode && (
                                <>
                                  <h6
                                    style={{
                                      background: "transparent",
                                      padding: 0,
                                      marginBottom: 10,
                                      color: "#3D3F5C",
                                      fontSize: 24,
                                      fontWeight: 600,
                                    }}
                                  >
                                    Primary Point of Contact
                                  </h6>
                                  <Row>
                                    <Col md={6}>
                                      <Input
                                        label="Name"
                                        placeholder="Enter Name"
                                        name="primaryContact.name"
                                        value={values.primaryContact.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.primaryContact?.name}
                                        touched={touched.primaryContact?.name}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <PhoneNumberInput
                                        label="Contact"
                                        name="primaryContact.contact"
                                        value={values.primaryContact.contact}
                                        setFieldValue={setFieldValue}
                                        onBlur={handleBlur}
                                        error={errors.primaryContact?.contact}
                                        touched={touched.primaryContact?.contact}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Select
                                        label={requiredLabel("Designation")}
                                        placeholder="Select Designation"
                                        options={designationOptions}
                                        value={
                                          designationOptions.find(
                                            (opt) =>
                                              opt.value ===
                                              values.primaryContact.designation
                                          ) || null
                                        }
                                        name="primaryContact.designation"
                                        onChange={(opt) =>
                                          setFieldValue(
                                            "primaryContact.designation",
                                            opt ? opt.value : ""
                                          )
                                        }
                                        onBlur={() =>
                                          setFieldTouched(
                                            "primaryContact.designation",
                                            true
                                          )
                                        }
                                        error={errors.primaryContact?.designation}
                                        touched={touched.primaryContact?.designation}
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <Input
                                        label="Email Address"
                                        placeholder="Enter Email"
                                        name="primaryContact.email"
                                        value={values.primaryContact.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.primaryContact?.email}
                                        touched={touched.primaryContact?.email}
                                      />
                                    </Col>
                                  </Row>

                                  {hasSecondaryContacts && (
                                    <div
                                      style={{
                                        borderTop: "1px solid #DDE3F0",
                                        marginTop: 16,
                                        marginBottom: 14,
                                      }}
                                    />
                                  )}
                                </>
                              )}

                              {isEditMode ? (
                                <>
                                  {(() => {
                                    const contactData = [
                                      ...(values.primaryContact && (values.primaryContact.name || values.primaryContact.contact)
                                        ? [{ ...values.primaryContact, isPrimary: true }]
                                        : []),
                                      ...safeArray(values.secondaryContacts).map((item) => ({ ...item, isPrimary: false })),
                                    ];
                                    return (
                                      <StickyActionsTable
                                        columns={contactTableColumns}
                                        data={contactData}
                                        noDataMessage="No points of contact found for this customer."
                                        actionsRenderer={(row) => (
                                          <div className="table_action_btn">
                                            <button
                                              type="button"
                                              className="edit_btn"
                                              title="Edit"
                                              onClick={() =>
                                                openEditSecondaryTableForm(row, row.isPrimary)
                                              }
                                            >
                                              <PiPencilSimpleLine />
                                            </button>
                                            {!row.isPrimary && (
                                              <button
                                                type="button"
                                                className="delete_btn"
                                                title="Delete"
                                                onClick={() =>
                                                  openSecondaryDeleteModal(row)
                                                }
                                              >
                                                <MdDeleteOutline />
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      />
                                    );
                                  })()}

                                  {showSecondaryForm && (
                                    <div className="service_line_form mt-3">
                                      <div>
                                        <div className="service_line_item_header">
                                          <h5>
                                            {editingSecondaryId === "primary"
                                              ? "Update Primary Contact"
                                              : editingSecondaryId
                                                ? "Update Secondary Contact"
                                                : hasPrimaryContact
                                                  ? "Add Secondary Contact"
                                                  : "Add Primary Contact"}
                                          </h5>
                                          <div
                                            className="save_button_container d-flex align-items-center"
                                            style={{ gap: 8 }}
                                          >
                                            <button
                                              type="button"
                                              className="main_btn"
                                              onClick={saveSecondaryTableRow}
                                            >
                                              {editingSecondaryId ? "Update" : "Save"}
                                            </button>
                                            <button
                                              type="button"
                                              className="cancel_btn"
                                              onClick={resetSecondaryTableForm}
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        </div>
                                        <Row>
                                          <Col md={6}>
                                            <Input
                                              label="Name"
                                              placeholder="Enter Name"
                                              name="name"
                                              value={secondaryDraft.name}
                                              onChange={(event) =>
                                                handleSecondaryDraftChange(
                                                  "name",
                                                  event.target.value
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={secondaryDraftErrors.name}
                                              touched={Boolean(
                                                secondaryDraftErrors.name
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <PhoneNumberInput
                                              label="Contact"
                                              name="contact"
                                              value={secondaryDraft.contact}
                                              setFieldValue={(_, value) =>
                                                handleSecondaryDraftChange(
                                                  "contact",
                                                  value
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={secondaryDraftErrors.contact}
                                              touched={Boolean(
                                                secondaryDraftErrors.contact
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <Select
                                              label={requiredLabel("Designation")}
                                              placeholder="Select Designation"
                                              options={designationOptions}
                                              value={pickOption(
                                                designationOptions,
                                                secondaryDraft.designation
                                              )}
                                              name="designation"
                                              onChange={(opt) =>
                                                handleSecondaryDraftChange(
                                                  "designation",
                                                  opt ? opt.value : ""
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={secondaryDraftErrors.designation}
                                              touched={Boolean(
                                                secondaryDraftErrors.designation
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <Input
                                              label="Email Address"
                                              placeholder="Enter Email"
                                              name="email"
                                              value={secondaryDraft.email}
                                              onChange={(event) =>
                                                handleSecondaryDraftChange(
                                                  "email",
                                                  event.target.value
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={secondaryDraftErrors.email}
                                              touched={Boolean(
                                                secondaryDraftErrors.email
                                              )}
                                            />
                                          </Col>
                                        </Row>
                                      </div>
                                    </div>
                                  )}

                                  {!showSecondaryForm && (
                                    <button
                                      type="button"
                                      onClick={openCreateSecondaryTableForm}
                                      style={getAddActionStyle(false, { marginTop: 14 })}
                                    >
                                      <FiPlus style={{ fontSize: 20 }} />
                                      {hasPrimaryContact
                                        ? "Add Contact Person"
                                        : "Add Primary Contact"}
                                    </button>
                                  )}
                                </>
                              ) : (
                                <>
                                  {safeArray(values.secondaryContacts).map((item, index) => (
                                    <div key={item.id} style={{ marginBottom: 12 }}>
                                      <h6
                                        style={{
                                          background: "transparent",
                                          padding: 0,
                                          marginBottom: 10,
                                          color: "#3D3F5C",
                                          fontSize: 20,
                                          fontWeight: 500,
                                        }}
                                      >
                                        Secondary Point of Contact
                                      </h6>
                                      <Row>
                                        <Col md={6}>
                                          <Input
                                            label="Name"
                                            placeholder="Enter Name"
                                            name={`secondaryContacts[${index}].name`}
                                            value={item.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors.secondaryContacts?.[index]?.name}
                                            touched={touched.secondaryContacts?.[index]?.name}
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <PhoneNumberInput
                                            label="Contact"
                                            name={`secondaryContacts[${index}].contact`}
                                            value={item.contact}
                                            setFieldValue={setFieldValue}
                                            onBlur={handleBlur}
                                            error={errors.secondaryContacts?.[index]?.contact}
                                            touched={touched.secondaryContacts?.[index]?.contact}
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <Select
                                            label={requiredLabel("Designation")}
                                            placeholder="Select Designation"
                                            options={designationOptions}
                                            value={
                                              designationOptions.find(
                                                (opt) => opt.value === item.designation
                                              ) || null
                                            }
                                            name={`secondaryContacts[${index}].designation`}
                                            onChange={(opt) =>
                                              setFieldValue(
                                                `secondaryContacts[${index}].designation`,
                                                opt ? opt.value : ""
                                              )
                                            }
                                            onBlur={() =>
                                              setFieldTouched(
                                                `secondaryContacts[${index}].designation`,
                                                true
                                              )
                                            }
                                            error={errors.secondaryContacts?.[index]?.designation}
                                            touched={
                                              touched.secondaryContacts?.[index]?.designation
                                            }
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <Input
                                            label="Email Address"
                                            placeholder="Enter Email"
                                            name={`secondaryContacts[${index}].email`}
                                            value={item.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors.secondaryContacts?.[index]?.email}
                                            touched={touched.secondaryContacts?.[index]?.email}
                                            required={false}
                                          />
                                        </Col>
                                      </Row>

                                      <button
                                        type="button"
                                        onClick={() => removeSecondary(item.id)}
                                        style={deleteActionStyle}
                                      >
                                        <MdDeleteOutline style={{ fontSize: 18 }} />
                                        Delete Contact Person
                                      </button>

                                      {index !== safeArray(values.secondaryContacts).length - 1 && (
                                        <div
                                          style={{
                                            borderTop: "1px solid #DDE3F0",
                                            marginTop: 14,
                                            marginBottom: 14,
                                          }}
                                        />
                                      )}
                                    </div>
                                  ))}

                                  <button
                                    type="button"
                                    onClick={addSecondary}
                                    style={getAddActionStyle(false, {
                                      marginTop: hasSecondaryContacts ? 6 : 12,
                                    })}
                                  >
                                    <FiPlus style={{ fontSize: 20 }} />
                                    Add Contact Person
                                  </button>
                                </>
                              )}
                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-3">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={prevStep}
                                  >
                                    Previous
                                  </button>
                                  <button
                                    type="button"
                                    className="main_btn"
                                    onClick={nextStep}
                                  >
                                    Next
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {showGeneralDetailSections && (isEditMode || currentStep === 2) && (
                            <div style={isEditMode ? sectionCardStyle : undefined}>
                              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                <div>
                                  {isEditMode && (
                                    <h6 style={sectionHeadingStyle}>Location</h6>
                                  )}
                                  <p
                                    style={{
                                      color: "#3D3F5C",
                                      fontSize: 15,
                                      marginBottom: 0,
                                    }}
                                  >
                                    Add at least {minimumWarehouseCount} customer locations
                                    by city/address. You can add more if needed.
                                  </p>
                                </div>
                                <div className="services_buttons d-flex align-items-center gap-2">
                                  <ExportButton
                                    type="pdf"
                                    onClick={() =>
                                      handleLocationsExport(
                                        "pdf",
                                        values.warehouses,
                                        values.customerName
                                      )
                                    }
                                    disabled={
                                      safeArray(values.warehouses).length === 0 ||
                                      Boolean(locationsExportLoading)
                                    }
                                    loading={locationsExportLoading === "pdf"}
                                    title={
                                      safeArray(values.warehouses).length === 0
                                        ? "No locations to export"
                                        : undefined
                                    }
                                  />
                                  <ExportButton
                                    type="excel"
                                    onClick={() =>
                                      handleLocationsExport(
                                        "excel",
                                        values.warehouses,
                                        values.customerName
                                      )
                                    }
                                    disabled={
                                      safeArray(values.warehouses).length === 0 ||
                                      Boolean(locationsExportLoading)
                                    }
                                    loading={locationsExportLoading === "excel"}
                                    title={
                                      safeArray(values.warehouses).length === 0
                                        ? "No locations to export"
                                        : undefined
                                    }
                                  />
                                </div>
                              </div>

                              <div
                                style={{
                                  borderTop: "1px solid #DDE3F0",
                                  marginTop: 8,
                                  marginBottom: 18,
                                }}
                              />

                              {isEditMode ? (
                                <>
                                  <StickyActionsTable
                                    columns={locationTableColumns}
                                    data={safeArray(values.warehouses)}
                                    noDataMessage="No locations found for this customer."
                                    expandedRowRenderer={(row) =>
                                      showLocationForm &&
                                        isEditingLocationTableRow &&
                                        String(row?.id) === String(editingLocationId)
                                        ? renderLocationTableForm(true)
                                        : null
                                    }
                                    actionsRenderer={(row) => (
                                      <div className="table_action_btn">
                                        <button
                                          type="button"
                                          className="edit_btn"
                                          title="Edit"
                                          onClick={() =>
                                            openEditLocationTableForm(row)
                                          }
                                        >
                                          <PiPencilSimpleLine />
                                        </button>
                                        <button
                                          type="button"
                                          className="delete_btn"
                                          title={
                                            safeArray(values.warehouses).length <=
                                              minimumWarehouseCount
                                              ? `At least ${minimumWarehouseCount} locations are required`
                                              : "Delete"
                                          }
                                          disabled={
                                            safeArray(values.warehouses).length <=
                                            minimumWarehouseCount
                                          }
                                          onClick={() =>
                                            openLocationDeleteModal(row)
                                          }
                                        >
                                          <MdDeleteOutline />
                                        </button>
                                      </div>
                                    )}
                                  />

                                  {showLocationForm && !isEditingLocationTableRow && (
                                    <div className="service_line_form mt-3">
                                      <div>
                                        <div className="service_line_item_header">
                                          <h5>
                                            {editingLocationId
                                              ? "Update Location"
                                              : "Add Location"}
                                          </h5>
                                          <div
                                            className="save_button_container d-flex align-items-center"
                                            style={{ gap: 8 }}
                                          >
                                            <button
                                              type="button"
                                              className="main_btn"
                                              onClick={saveLocationTableRow}
                                              disabled={locationSubmitting}
                                            >
                                              {locationSubmitting ? "Submitting..." : (editingLocationId ? "Update" : "Save")}
                                            </button>
                                            <button
                                              type="button"
                                              className="cancel_btn"
                                              onClick={resetLocationTableForm}
                                              disabled={locationSubmitting}
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        </div>
                                        <Row>
                                          <Col md={6}>
                                            <Input
                                              label="Location Name"
                                              placeholder="Enter Location"
                                              name="warehouseName"
                                              value={locationDraft.warehouseName}
                                              onChange={(event) =>
                                                handleLocationDraftChange(
                                                  "warehouseName",
                                                  event.target.value
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={locationDraftErrors.warehouseName}
                                              touched={Boolean(
                                                locationDraftErrors.warehouseName
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <Select
                                              label={requiredLabel("City")}
                                              placeholder="Select City"
                                              options={warehouseCityOptions}
                                              loading={dropdownLoading}
                                              value={pickOption(
                                                warehouseCityOptions,
                                                locationDraft.city
                                              )}
                                              name="city"
                                              onChange={(opt) =>
                                                handleLocationDraftChange(
                                                  "city",
                                                  opt ? opt.value : ""
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={locationDraftErrors.city}
                                              touched={Boolean(
                                                locationDraftErrors.city
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <PhoneNumberInput
                                              label="Contact"
                                              name="contact"
                                              value={locationDraft.contact}
                                              setFieldValue={(_, value) =>
                                                handleLocationDraftChange(
                                                  "contact",
                                                  value
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={locationDraftErrors.contact}
                                              touched={Boolean(
                                                locationDraftErrors.contact
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <Input
                                              label="Contact Person Name"
                                              placeholder="Enter Contact Person Name"
                                              name="contactPersonName"
                                              value={locationDraft.contactPersonName}
                                              onChange={(event) =>
                                                handleLocationDraftChange(
                                                  "contactPersonName",
                                                  event.target.value
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={
                                                locationDraftErrors.contactPersonName
                                              }
                                              touched={Boolean(
                                                locationDraftErrors.contactPersonName
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <PhoneNumberInput
                                              label="Contact Person Number"
                                              name="contactPersonNumber"
                                              value={
                                                locationDraft.contactPersonNumber
                                              }
                                              setFieldValue={(_, value) =>
                                                handleLocationDraftChange(
                                                  "contactPersonNumber",
                                                  value
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={
                                                locationDraftErrors.contactPersonNumber
                                              }
                                              touched={Boolean(
                                                locationDraftErrors.contactPersonNumber
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <Select
                                              label={requiredLabel("Status")}
                                              placeholder="Select Status"
                                              options={locationStatusOptions}
                                              value={
                                                locationStatusOptions.find(
                                                  (opt) =>
                                                    opt.value ===
                                                    resolveWarehouseStatus(locationDraft.status)
                                                ) || null
                                              }
                                              name="status"
                                              onChange={(opt) =>
                                                handleLocationDraftChange(
                                                  "status",
                                                  opt ? opt.value : ""
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={locationDraftErrors.status}
                                              touched={Boolean(locationDraftErrors.status)}
                                            />
                                          </Col>
                                          <Col md={12}>
                                            <GoogleMapInput
                                              label="Address"
                                              placeholder="Search address..."
                                              name="warehouseAddress"
                                              value={locationDraft.warehouseAddress}
                                              setFieldValue={(fieldName, value) =>
                                                handleLocationDraftChange(
                                                  fieldName,
                                                  value
                                                )
                                              }
                                              onChange={(
                                                address,
                                                latitude,
                                                longitude,
                                                rawResult
                                              ) => {
                                                handleLocationDraftChange(
                                                  "warehouseAddress",
                                                  address || ""
                                                );
                                                handleLocationDraftChange(
                                                  "warehouseLatitude",
                                                  latitude || ""
                                                );
                                                handleLocationDraftChange(
                                                  "warehouseLongitude",
                                                  longitude || ""
                                                );

                                                const resolvedProv = detectProvince(address, rawResult);
                                                if (resolvedProv) {
                                                  handleLocationDraftChange(
                                                    "province",
                                                    resolvedProv
                                                  );
                                                }
                                              }}
                                              error={
                                                locationDraftErrors.warehouseAddress
                                              }
                                              touched={Boolean(
                                                locationDraftErrors.warehouseAddress
                                              )}
                                              storeAs="string"
                                              latitudeFieldName="warehouseLatitude"
                                              longitudeFieldName="warehouseLongitude"
                                            />
                                          </Col>
                                        </Row>
                                      </div>
                                    </div>
                                  )}

                                  {!showLocationForm && (
                                    <button
                                      type="button"
                                      onClick={openCreateLocationTableForm}
                                      style={getAddActionStyle(false, { marginTop: 14 })}
                                    >
                                      <FiPlus style={{ fontSize: 20 }} />
                                      Add Customer Location
                                    </button>
                                  )}
                                </>
                              ) : (
                                <>
                                  {safeArray(values.warehouses).map((item, index) => (
                                    <div key={item.id} style={{ marginBottom: 12 }}>
                                      <Row>
                                        <Col md={6}>
                                          <Input
                                            label="Location Name"
                                            placeholder="Enter Location"
                                            name={`warehouses[${index}].warehouseName`}
                                            value={item.warehouseName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                              errors.warehouses?.[index]?.warehouseName
                                            }
                                            touched={
                                              touched.warehouses?.[index]?.warehouseName
                                            }
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <Select
                                            label={requiredLabel("City")}
                                            placeholder="Select City"
                                            options={warehouseCityOptions}
                                            loading={dropdownLoading}
                                            value={pickOption(
                                              warehouseCityOptions,
                                              item.city
                                            )}
                                            name={`warehouses[${index}].city`}
                                            onChange={(opt) =>
                                              setFieldValue(
                                                `warehouses[${index}].city`,
                                                opt ? opt.value : ""
                                              )
                                            }
                                            onBlur={() =>
                                              setFieldTouched(
                                                `warehouses[${index}].city`,
                                                true
                                              )
                                            }
                                            error={errors.warehouses?.[index]?.city}
                                            touched={
                                              touched.warehouses?.[index]?.city
                                            }
                                          />
                                        </Col>
                                        <Col md={6} style={{ display: "none" }}>
                                          <Select
                                            label={requiredLabel("Province")}
                                            placeholder="Select Province"
                                            options={provinceOptions}
                                            value={getProvinceOption(item.province)}
                                            name={`warehouses[${index}].province`}
                                            onChange={(opt) =>
                                              setFieldValue(
                                                `warehouses[${index}].province`,
                                                opt ? opt.value : ""
                                              )
                                            }
                                            onBlur={() =>
                                              setFieldTouched(
                                                `warehouses[${index}].province`,
                                                true
                                              )
                                            }
                                            error={errors.warehouses?.[index]?.province}
                                            touched={
                                              touched.warehouses?.[index]?.province
                                            }
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <PhoneNumberInput
                                            label="Contact"
                                            name={`warehouses[${index}].contact`}
                                            value={item.contact}
                                            setFieldValue={setFieldValue}
                                            onBlur={handleBlur}
                                            error={
                                              errors.warehouses?.[index]?.contact
                                            }
                                            touched={
                                              touched.warehouses?.[index]?.contact
                                            }
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <Input
                                            label="Contact Person Name"
                                            placeholder="Enter Contact Person Name"
                                            name={`warehouses[${index}].contactPersonName`}
                                            value={item.contactPersonName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                              errors.warehouses?.[index]
                                                ?.contactPersonName
                                            }
                                            touched={
                                              touched.warehouses?.[index]
                                                ?.contactPersonName
                                            }
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <PhoneNumberInput
                                            label="Contact Person Number"
                                            name={`warehouses[${index}].contactPersonNumber`}
                                            value={item.contactPersonNumber}
                                            setFieldValue={setFieldValue}
                                            onBlur={handleBlur}
                                            error={
                                              errors.warehouses?.[index]
                                                ?.contactPersonNumber
                                            }
                                            touched={
                                              touched.warehouses?.[index]
                                                ?.contactPersonNumber
                                            }
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <Select
                                            label={requiredLabel("Status")}
                                            placeholder="Select Status"
                                            options={locationStatusOptions}
                                            value={
                                              locationStatusOptions.find(
                                                (opt) =>
                                                  opt.value ===
                                                  resolveWarehouseStatus(item.status)
                                              ) || null
                                            }
                                            name={`warehouses[${index}].status`}
                                            onChange={(opt) =>
                                              setFieldValue(
                                                `warehouses[${index}].status`,
                                                opt ? opt.value : ""
                                              )
                                            }
                                            onBlur={() =>
                                              setFieldTouched(
                                                `warehouses[${index}].status`,
                                                true
                                              )
                                            }
                                            error={errors.warehouses?.[index]?.status}
                                            touched={touched.warehouses?.[index]?.status}
                                          />
                                        </Col>
                                        <Col md={12}>
                                          <GoogleMapInput
                                            label="Address"
                                            placeholder="Search address..."
                                            name={`warehouses[${index}].warehouseAddress`}
                                            value={item.warehouseAddress}
                                            setFieldValue={setFieldValue}
                                            onChange={(address, latitude, longitude, rawResult) => {
                                              setFieldTouched(
                                                `warehouses[${index}].warehouseAddress`,
                                                false,
                                                false
                                              );
                                              setFieldValue(
                                                `warehouses[${index}].warehouseAddress`,
                                                address || ""
                                              );
                                              setFieldValue(
                                                `warehouses[${index}].warehouseLatitude`,
                                                latitude || ""
                                              );
                                              setFieldValue(
                                                `warehouses[${index}].warehouseLongitude`,
                                                longitude || ""
                                              );
                                              const resolvedProv = detectProvince(address, rawResult);
                                              if (resolvedProv) {
                                                setFieldValue(
                                                  "warehouses[" + index + "].province",
                                                  resolvedProv
                                                );
                                              }
                                            }}
                                            error={
                                              errors.warehouses?.[index]
                                                ?.warehouseAddress
                                            }
                                            touched={
                                              touched.warehouses?.[index]
                                                ?.warehouseAddress
                                            }
                                            storeAs="string"
                                            latitudeFieldName={`warehouses[${index}].warehouseLatitude`}
                                            longitudeFieldName={`warehouses[${index}].warehouseLongitude`}
                                          />
                                        </Col>
                                      </Row>

                                      {safeArray(values.warehouses).length >
                                        minimumWarehouseCount && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              removeWarehouseRow(item.id)
                                            }
                                            style={deleteActionStyle}
                                          >
                                            <MdDeleteOutline
                                              style={{ fontSize: 18 }}
                                            />
                                            Delete Location
                                          </button>
                                        )}

                                      <div
                                        style={{
                                          borderTop: "1px solid #DDE3F0",
                                          marginTop: 14,
                                          marginBottom: 14,
                                        }}
                                      />
                                    </div>
                                  ))}

                                  <button
                                    type="button"
                                    onClick={addWarehouseRow}
                                    style={getAddActionStyle(false)}
                                  >
                                    <FiPlus style={{ fontSize: 20 }} />
                                    Add Customer Location
                                  </button>
                                </>
                              )}

                              {touched.warehouses &&
                                typeof errors.warehouses === "string" && (
                                  <div
                                    className="error-message"
                                    style={{
                                      color: "#ff0000de",
                                      fontSize: 12,
                                      marginTop: 4,
                                    }}
                                  >
                                    {errors.warehouses}
                                  </div>
                                )}

                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-3">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={prevStep}
                                  >
                                    Previous
                                  </button>
                                  <button
                                    type="button"
                                    className="main_btn"
                                    onClick={nextStep}
                                  >
                                    Next
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {showGeneralDetailSections && (isEditMode || currentStep === 3) && (
                            <div style={isEditMode ? sectionCardStyle : undefined}>
                              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                <div>
                                  {isEditMode && (
                                    <h6 style={sectionHeadingStyle}>Products</h6>
                                  )}
                                  <p
                                    style={{
                                      color: "#3D3F5C",
                                      fontSize: 15,
                                      marginBottom: 0,
                                    }}
                                  >
                                    Add customer product details. You can add more products if
                                    needed.
                                  </p>
                                </div>
                                <div className="services_buttons d-flex align-items-center gap-2">
                                  <ExportButton
                                    type="pdf"
                                    onClick={() =>
                                      handleProductsExport(
                                        "pdf",
                                        values.products,
                                        values.customerName
                                      )
                                    }
                                    disabled={
                                      safeArray(values.products).length === 0 ||
                                      Boolean(productsExportLoading)
                                    }
                                    loading={productsExportLoading === "pdf"}
                                    title={
                                      safeArray(values.products).length === 0
                                        ? "No products to export"
                                        : undefined
                                    }
                                  />
                                  <ExportButton
                                    type="excel"
                                    onClick={() =>
                                      handleProductsExport(
                                        "excel",
                                        values.products,
                                        values.customerName
                                      )
                                    }
                                    disabled={
                                      safeArray(values.products).length === 0 ||
                                      Boolean(productsExportLoading)
                                    }
                                    loading={productsExportLoading === "excel"}
                                    title={
                                      safeArray(values.products).length === 0
                                        ? "No products to export"
                                        : undefined
                                    }
                                  />
                                </div>
                              </div>

                              <div
                                style={{
                                  borderTop: "1px solid #DDE3F0",
                                  marginTop: 8,
                                  marginBottom: 18,
                                }}
                              />

                              {isEditMode ? (
                                <>
                                  <StickyActionsTable
                                    columns={productTableColumns}
                                    data={safeArray(values.products)}
                                    noDataMessage="No products found for this customer."
                                    expandedRowRenderer={(row) =>
                                      showProductForm &&
                                        isEditingProductTableRow &&
                                        String(row?.id) === String(editingProductId)
                                        ? renderProductTableForm(true)
                                        : null
                                    }
                                    actionsRenderer={(row) => (
                                      <div className="table_action_btn">
                                        {isSavedProduct(row) ? (
                                          <>
                                            <button
                                              type="button"
                                              className="edit_btn"
                                              title="View Rate History"
                                              onClick={() => openProductHistoryModal(row)}
                                            >
                                              <FiEye />
                                            </button>
                                            <button
                                              type="button"
                                              className="edit_btn"
                                              title="Edit Product Value/Rate"
                                              onClick={() => openEditProductRateModal(row)}
                                            >
                                              <PiMoney />
                                            </button>
                                            <button
                                              type="button"
                                              className="edit_btn"
                                              title="Edit Product Details"
                                              onClick={() => openEditProductTableForm(row)}
                                            >
                                              <PiPencilSimpleLine />
                                            </button>
                                          </>
                                        ) : (
                                          <button
                                            type="button"
                                            className="edit_btn"
                                            title="Edit Details"
                                            onClick={() =>
                                              openEditProductTableForm(row)
                                            }
                                          >
                                            <PiPencilSimpleLine />
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          className="delete_btn"
                                          title={
                                            safeArray(values.products).length <=
                                              minimumProductCount
                                              ? `At least ${minimumProductCount} product is required`
                                              : "Delete"
                                          }
                                          disabled={
                                            safeArray(values.products).length <=
                                            minimumProductCount
                                          }
                                          onClick={() =>
                                            openProductDeleteModal(row)
                                          }
                                        >
                                          <MdDeleteOutline />
                                        </button>
                                      </div>
                                    )}
                                  />

                                  {showProductForm && !isEditingProductTableRow && (
                                    <div className="service_line_form mt-3">
                                      <div>
                                        <div className="service_line_item_header">
                                          <h5>
                                            {editingProductId
                                              ? "Update Product"
                                              : "Add Product"}
                                          </h5>
                                          <div
                                            className="save_button_container d-flex align-items-center"
                                            style={{ gap: 8 }}
                                          >
                                            <button
                                              type="button"
                                              className="main_btn"
                                              onClick={saveProductTableRow}
                                              disabled={productSubmitting}
                                            >
                                              {productSubmitting ? "Submitting..." : (editingProductId ? "Update" : "Save")}
                                            </button>
                                            <button
                                              type="button"
                                              className="cancel_btn"
                                              onClick={resetProductTableForm}
                                              disabled={productSubmitting}
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        </div>
                                        <Row>
                                          <Col md={6}>
                                            <Input
                                              label="Product Name"
                                              placeholder="Enter Product Name"
                                              name="name"
                                              value={productDraft.name}
                                              onChange={(event) =>
                                                handleProductDraftChange(
                                                  "name",
                                                  event.target.value
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={productDraftErrors.name}
                                              touched={Boolean(
                                                productDraftErrors.name
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <Select
                                              label={requiredLabel("Unit")}
                                              placeholder="Select Unit"
                                              options={partUnitOptions}
                                              loading={dropdownLoading}
                                              value={pickOption(
                                                partUnitOptions,
                                                productDraft.unit
                                              )}
                                              name="unit"
                                              onChange={(opt) =>
                                                handleProductDraftChange(
                                                  "unit",
                                                  opt ? opt.value : ""
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={productDraftErrors.unit}
                                              touched={Boolean(
                                                productDraftErrors.unit
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <Select
                                              label={requiredLabel("Product Type")}
                                              placeholder="Select Product Type"
                                              options={productTypeOptions}
                                              value={pickOption(
                                                productTypeOptions,
                                                productDraft.productType
                                              )}
                                              name="productType"
                                              onChange={(opt) =>
                                                handleProductDraftChange(
                                                  "productType",
                                                  opt ? opt.value : ""
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={productDraftErrors.productType}
                                              touched={Boolean(
                                                productDraftErrors.productType
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <Input
                                              label="Product Value"
                                              type="number"
                                              placeholder="Enter Product Value"
                                              name="productValue"
                                              value={productDraft.productValue}
                                              onChange={(event) =>
                                                handleProductDraftChange(
                                                  "productValue",
                                                  event.target.value
                                                )
                                              }
                                              onBlur={() => { }}
                                              disabled={editingProductId !== null}
                                              error={productDraftErrors.productValue}
                                              touched={Boolean(
                                                productDraftErrors.productValue
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <Select
                                              label={requiredLabel("Insurance Cover Type")}
                                              placeholder="Select Insurance Cover Type"
                                              options={insuranceCoverTypeOptions}
                                              value={pickOption(
                                                insuranceCoverTypeOptions,
                                                productDraft.insuranceCoverType
                                              )}
                                              name="insuranceCoverType"
                                              onChange={(opt) =>
                                                handleProductDraftChange(
                                                  "insuranceCoverType",
                                                  opt ? opt.value : ""
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={productDraftErrors.insuranceCoverType}
                                              touched={Boolean(
                                                productDraftErrors.insuranceCoverType
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <Select
                                              label={requiredLabel("Insurance Status")}
                                              placeholder="Select Insurance Status"
                                              options={insuranceStatusOptions}
                                              value={pickOption(
                                                insuranceStatusOptions,
                                                productDraft.insuranceStatus
                                              )}
                                              name="insuranceStatus"
                                              onChange={(opt) =>
                                                handleProductDraftChange(
                                                  "insuranceStatus",
                                                  opt ? opt.value : ""
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={productDraftErrors.insuranceStatus}
                                              touched={Boolean(
                                                productDraftErrors.insuranceStatus
                                              )}
                                            />
                                          </Col>
                                          <Col md={6}>
                                            <Input
                                              label="Insurance Cover Value"
                                              type="text"
                                              placeholder="Enter Insurance Cover Value"
                                              name="insuranceCoverValue"
                                              value={productDraft.insuranceCoverValue}
                                              onChange={(event) =>
                                                handleProductDraftChange(
                                                  "insuranceCoverValue",
                                                  event.target.value
                                                )
                                              }
                                              onBlur={() => { }}
                                              error={productDraftErrors.insuranceCoverValue}
                                              touched={Boolean(
                                                productDraftErrors.insuranceCoverValue
                                              )}
                                            />
                                          </Col>
                                        </Row>
                                      </div>
                                    </div>
                                  )}

                                  {!showProductForm && (
                                    <button
                                      type="button"
                                      onClick={openCreateProductTableForm}
                                      style={getAddActionStyle(false, { marginTop: 14 })}
                                    >
                                      <FiPlus style={{ fontSize: 20 }} />
                                      Add Customer Product
                                    </button>
                                  )}
                                </>
                              ) : (
                                <>
                                  {safeArray(values.products).map((item, index) => (
                                    <div key={item.id} style={{ marginBottom: 12 }}>
                                      <Row>
                                        <Col md={6}>
                                          <Input
                                            label="Product Name"
                                            placeholder="Enter Product Name"
                                            name={`products[${index}].name`}
                                            value={item.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors.products?.[index]?.name}
                                            touched={touched.products?.[index]?.name}
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <Select
                                            label={requiredLabel("Unit")}
                                            placeholder="Select Unit"
                                            options={partUnitOptions}
                                            loading={dropdownLoading}
                                            value={pickOption(partUnitOptions, item.unit)}
                                            name={`products[${index}].unit`}
                                            onChange={(opt) =>
                                              setFieldValue(
                                                `products[${index}].unit`,
                                                opt ? opt.value : ""
                                              )
                                            }
                                            onBlur={() =>
                                              setFieldTouched(`products[${index}].unit`, true)
                                            }
                                            error={errors.products?.[index]?.unit}
                                            touched={touched.products?.[index]?.unit}
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <Select
                                            label={requiredLabel("Product Type")}
                                            placeholder="Select Product Type"
                                            options={productTypeOptions}
                                            value={pickOption(productTypeOptions, item.productType)}
                                            name={`products[${index}].productType`}
                                            onChange={(opt) =>
                                              setFieldValue(
                                                `products[${index}].productType`,
                                                opt ? opt.value : ""
                                              )
                                            }
                                            onBlur={() =>
                                              setFieldTouched(
                                                `products[${index}].productType`,
                                                true
                                              )
                                            }
                                            error={errors.products?.[index]?.productType}
                                            touched={touched.products?.[index]?.productType}
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <Input
                                            label="Product Value"
                                            type="number"
                                            placeholder="Enter Product Value"
                                            name={`products[${index}].productValue`}
                                            value={item.productValue}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors.products?.[index]?.productValue}
                                            touched={touched.products?.[index]?.productValue}
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <Select
                                            label={requiredLabel("Insurance Cover Type")}
                                            placeholder="Select Insurance Cover Type"
                                            options={insuranceCoverTypeOptions}
                                            value={pickOption(
                                              insuranceCoverTypeOptions,
                                              item.insuranceCoverType
                                            )}
                                            name={`products[${index}].insuranceCoverType`}
                                            onChange={(opt) =>
                                              setFieldValue(
                                                `products[${index}].insuranceCoverType`,
                                                opt ? opt.value : ""
                                              )
                                            }
                                            onBlur={() =>
                                              setFieldTouched(
                                                `products[${index}].insuranceCoverType`,
                                                true
                                              )
                                            }
                                            error={errors.products?.[index]?.insuranceCoverType}
                                            touched={
                                              touched.products?.[index]?.insuranceCoverType
                                            }
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <Select
                                            label={requiredLabel("Insurance Status")}
                                            placeholder="Select Insurance Status"
                                            options={insuranceStatusOptions}
                                            value={pickOption(
                                              insuranceStatusOptions,
                                              item.insuranceStatus
                                            )}
                                            name={`products[${index}].insuranceStatus`}
                                            onChange={(opt) =>
                                              setFieldValue(
                                                `products[${index}].insuranceStatus`,
                                                opt ? opt.value : ""
                                              )
                                            }
                                            onBlur={() =>
                                              setFieldTouched(
                                                `products[${index}].insuranceStatus`,
                                                true
                                              )
                                            }
                                            error={errors.products?.[index]?.insuranceStatus}
                                            touched={touched.products?.[index]?.insuranceStatus}
                                          />
                                        </Col>
                                        <Col md={6}>
                                          <Input
                                            label="Insurance Cover Value"
                                            type="text"
                                            placeholder="Enter Insurance Cover Value"
                                            name={`products[${index}].insuranceCoverValue`}
                                            value={item.insuranceCoverValue}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              if (val === "") {
                                                setFieldValue(`products[${index}].insuranceCoverValue`, "", true);
                                                return;
                                              }
                                              if (/^\d*\.?\d*$/.test(val)) {
                                                const num = parseFloat(val);
                                                if (isNaN(num) || num <= 100) {
                                                  setFieldValue(`products[${index}].insuranceCoverValue`, val, true);
                                                }
                                              }
                                            }}
                                            onBlur={() =>
                                              setFieldTouched(
                                                `products[${index}].insuranceCoverValue`,
                                                true,
                                                true
                                              )
                                            }
                                            error={errors.products?.[index]?.insuranceCoverValue}
                                            touched={
                                              touched.products?.[index]?.insuranceCoverValue
                                            }
                                          />
                                        </Col>
                                      </Row>

                                      {safeArray(values.products).length > minimumProductCount && (
                                        <button
                                          type="button"
                                          onClick={() => removeProductRow(item.id)}
                                          style={deleteActionStyle}
                                        >
                                          <MdDeleteOutline style={{ fontSize: 18 }} />
                                          Delete Product
                                        </button>
                                      )}

                                      <div
                                        style={{
                                          borderTop: "1px solid #DDE3F0",
                                          marginTop: 14,
                                          marginBottom: 14,
                                        }}
                                      />
                                    </div>
                                  ))}

                                  <button
                                    type="button"
                                    onClick={addProductRow}
                                    style={getAddActionStyle(false)}
                                  >
                                    <FiPlus style={{ fontSize: 20 }} />
                                    Add Customer Product
                                  </button>
                                </>
                              )}

                              {touched.products && typeof errors.products === "string" && (
                                <div
                                  className="error-message"
                                  style={{
                                    color: "#ff0000de",
                                    fontSize: 12,
                                    marginTop: 4,
                                  }}
                                >
                                  {errors.products}
                                </div>
                              )}

                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-3">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={prevStep}
                                  >
                                    Previous
                                  </button>
                                  <button
                                    type="button"
                                    className="main_btn"
                                    onClick={nextStep}
                                  >
                                    Next
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {showGeneralDetailSections && (isEditMode || currentStep === 4) && (
                            <>
                              <div style={sectionCardStyle}>
                                {isEditMode && (
                                  <h6 style={sectionHeadingStyle}>Attachments</h6>
                                )}
                                <p
                                  style={{
                                    color: "#3D3F5C",
                                    fontSize: 15,
                                    marginBottom: 10,
                                  }}
                                >
                                  You can attach up to 5 files, max 1MB each.
                                </p>
                                <FileUpload
                                  destination="3pl-customers"
                                  files={safeArray(values.attachments)}
                                  setFiles={(nextFiles) => {
                                    const resolved =
                                      typeof nextFiles === "function"
                                        ? nextFiles(safeArray(values.attachments))
                                        : nextFiles;
                                    setFieldValue("attachments", resolved);
                                  }}
                                  maxFiles={5}
                                  maxFileSize={1}
                                />
                                {touched.attachments && errors.attachments && (
                                  <div
                                    className="error-message"
                                    style={{
                                      color: "#ff0000de",
                                      fontSize: 12,
                                      marginTop: 4,
                                    }}
                                  >
                                    {errors.attachments}
                                  </div>
                                )}
                              </div>
                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-3">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={prevStep}
                                  >
                                    Previous
                                  </button>
                                  <button
                                    type="button"
                                    className="main_btn"
                                    onClick={nextStep}
                                  >
                                    Next
                                  </button>
                                </div>
                              )}
                            </>
                          )}

                          {showGeneralDetailSections && (isEditMode || currentStep === 5) && (
                            <>
                              <div style={{ ...sectionCardStyle, marginBottom: 0 }}>
                                {isEditMode && (
                                  <h6 style={sectionHeadingStyle}>Payment Mode</h6>
                                )}
                                <Row>
                                  <Col md={6}>
                                    <Select
                                      label={requiredLabel("Billing Frequency")}
                                      placeholder="Select Frequency"
                                      options={billingFrequencyOptions}
                                      value={
                                        billingFrequencyOptions.find(
                                          (opt) =>
                                            opt.value ===
                                            values.payment.billingFrequency
                                        ) || null
                                      }
                                      name="payment.billingFrequency"
                                      onChange={(opt) =>
                                        setFieldValue(
                                          "payment.billingFrequency",
                                          opt ? opt.value : ""
                                        )
                                      }
                                      onBlur={() =>
                                        setFieldTouched("payment.billingFrequency", true)
                                      }
                                      error={errors.payment?.billingFrequency}
                                      touched={touched.payment?.billingFrequency}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Select
                                      label={requiredLabel("Payment Method")}
                                      placeholder="Select Method"
                                      options={paymentMethodOptions}
                                      value={
                                        paymentMethodOptions.find(
                                          (opt) =>
                                            opt.value === values.payment.paymentMethod
                                        ) || null
                                      }
                                      name="payment.paymentMethod"
                                      onChange={(opt) => {
                                        const method = opt ? opt.value : "";
                                        setFieldValue("payment.paymentMethod", method);

                                        if (!["bank_transfer", "cash"].includes(method)) {
                                          setFieldValue("payment.paymentTerm", "");
                                        }

                                        if (method !== "bank_transfer") {
                                          setFieldValue("payment.bank", "");
                                          setFieldValue("payment.accountTitle", "");
                                          setFieldValue("payment.iban", "");
                                        }
                                      }}
                                      onBlur={() =>
                                        setFieldTouched("payment.paymentMethod", true)
                                      }
                                      error={errors.payment?.paymentMethod}
                                      touched={touched.payment?.paymentMethod}
                                    />
                                  </Col>

                                  {["bank_transfer", "cash"].includes(
                                    values.payment.paymentMethod
                                  ) && (
                                      <Col md={6}>
                                        <Select
                                          label={requiredLabel("Payment Term")}
                                          placeholder="Select Payment Term"
                                          options={paymentTermOptions}
                                          value={
                                            paymentTermOptions.find(
                                              (opt) =>
                                                opt.value === values.payment.paymentTerm
                                            ) || null
                                          }
                                          name="payment.paymentTerm"
                                          onChange={(opt) =>
                                            setFieldValue(
                                              "payment.paymentTerm",
                                              opt ? opt.value : ""
                                            )
                                          }
                                          onBlur={() =>
                                            setFieldTouched("payment.paymentTerm", true)
                                          }
                                          menuPlacement="auto"
                                          menuPortalTarget={selectMenuPortalTarget}
                                          error={errors.payment?.paymentTerm}
                                          touched={touched.payment?.paymentTerm}
                                        />
                                      </Col>
                                    )}

                                  {values.payment.paymentMethod === "bank_transfer" && (
                                    <>
                                      <Col md={6}>
                                        <Select
                                          label={requiredLabel("Bank")}
                                          placeholder="Select Bank"
                                          options={bankOptions}
                                          loading={dropdownLoading}
                                          value={pickOption(bankOptions, values.payment.bank)}
                                          name="payment.bank"
                                          onChange={(opt) =>
                                            setFieldValue(
                                              "payment.bank",
                                              opt ? opt.value : ""
                                            )
                                          }
                                          onBlur={() =>
                                            setFieldTouched("payment.bank", true)
                                          }
                                          error={errors.payment?.bank}
                                          touched={touched.payment?.bank}
                                        />
                                      </Col>
                                      <Col md={6}>
                                        <Input
                                          label="Account Title"
                                          placeholder="Enter Account Title"
                                          name="payment.accountTitle"
                                          value={values.payment.accountTitle}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={errors.payment?.accountTitle}
                                          touched={touched.payment?.accountTitle}
                                        />
                                      </Col>
                                      <Col md={6}>
                                        <Input
                                          label="IBAN / Account Number"
                                          placeholder="Enter IBAN / Account Number"
                                          name="payment.iban"
                                          value={values.payment.iban}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={errors.payment?.iban}
                                          touched={touched.payment?.iban}
                                        />
                                      </Col>
                                    </>
                                  )}
                                </Row>
                              </div>

                              {!isEditMode && (
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={prevStep}
                                  >
                                    Previous
                                  </button>
                                </div>
                              )}
                            </>
                          )}

                          {isRoutesTabActive && (
                            <>
                              <div style={sectionCardStyle}>
                                <h6 style={sectionHeadingStyle}>Routes List</h6>
                                <p
                                  style={{
                                    color: "#3D3F5C",
                                    fontSize: 15,
                                    marginBottom: 14,
                                  }}
                                >
                                  Routes are loaded from the customer routes list API.
                                  Create and edit actions open in a modal.
                                </p>

                                {!resolvedCustomerEntityId && (
                                  <div
                                    className="error-message"
                                    style={{ color: "#EF0000", marginBottom: 12 }}
                                  >
                                    Customer id is not available yet.
                                  </div>
                                )}

                                <NewTableComponent
                                  btnName="Create Route"
                                  pageName="customer-routes"
                                  showAddBtn={canManageRoutes}
                                  onAddClick={openCreateCustomerRouteModal}
                                  columns={routeColumns}
                                  data={paginatedRouteRows}
                                  currentPage={customerRouteLogsPage}
                                  setCurrentPage={setCustomerRouteLogsPage}
                                  limit={customerRouteLogsLimit}
                                  setLimit={setCustomerRouteLogsLimit}
                                  totalItems={routeTotalItems}
                                  totalPages={routeTotalPages}
                                  onSearch={setCustomerRouteLogsSearchText}
                                  searchValue={customerRouteLogsSearchText}
                                  loading={customerRoutesLoading}
                                  paginationLoading={customerRoutesLoading}
                                  paginationClassName="inline_pagination customer_routes_pagination"
                                  setSearchFilters={setCustomerRouteLogsSearchFilters}
                                  searchFilters={customerRouteLogsSearchFilters}
                                  tableClassName="transporters_table customer_routes_table"
                                  noDataComponent={
                                    <div className="table_no_records">
                                      No routes found for this customer yet
                                    </div>
                                  }
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                {!isRoutesTabActive && (
                  <Col md={3}>
                    <div
                      className="trip_notes_main_div"
                      style={{ minHeight: "80vh", height: "100%" }}
                    >
                      <h5>Notes</h5>
                      <div style={{ padding: "0 12px 12px" }}>
                        <ReactQuill
                          placeholder="Type your notes or additional info..."
                          modules={modules}
                          theme="snow"
                          value={values.notes || ""}
                          formats={formats}
                          onChange={(content) => setFieldValue("notes", content)}
                          className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                        />
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </div>

            <CustomModal
              show={customerRouteModalOpen}
              handleClose={
                customerRouteSubmitting ? () => { } : closeCustomerRouteModal
              }
              heading={
                customerRouteModalMode === "edit" ? "Edit Route" : "Create Route"
              }
              size="lg"
              dialogClassName="customer_route_modal_dialog"
              customHeaderCss="customer_route_modal_header"
              hideCloseButton={customerRouteSubmitting}
            >
              <div className="customer_route_modal_body">
                {customerRouteErrors.customerId && (
                  <div className="error-message customer_route_modal_error_banner">
                    {customerRouteErrors.customerId}
                  </div>
                )}

                <div className="customer_route_modal_grid">
                  <Select
                    label={requiredLabel("From Location")}
                    placeholder="Select from location"
                    options={routeFromWarehouseOptions}
                    loading={customerRouteLocationOptionsLoading}
                    value={pickOption(
                      routeFromWarehouseOptions,
                      customerRouteForm.fromWarehouseId
                    )}
                    name="fromWarehouseId"
                    isDisabled={
                      customerRouteModalMode === "edit" ||
                      customerRouteLocationOptionsLoading
                    }
                    onChange={(option) =>
                      setCustomerRouteForm((previous) => ({
                        ...previous,
                        fromWarehouseId: option ? option.value : "",
                      }))
                    }
                    onBlur={() => { }}
                    error={customerRouteErrors.fromWarehouseId}
                    touched={Boolean(customerRouteErrors.fromWarehouseId)}
                    isClearable={true}
                    menuPortalTarget={selectMenuPortalTarget}
                  />

                  <Select
                    label={requiredLabel("To Location")}
                    placeholder="Select to location"
                    options={routeToWarehouseOptions}
                    loading={customerRouteLocationOptionsLoading}
                    value={pickOption(
                      routeToWarehouseOptions,
                      customerRouteForm.toWarehouseId
                    )}
                    name="toWarehouseId"
                    isDisabled={
                      customerRouteModalMode === "edit" ||
                      customerRouteLocationOptionsLoading
                    }
                    onChange={(option) =>
                      setCustomerRouteForm((previous) => ({
                        ...previous,
                        toWarehouseId: option ? option.value : "",
                      }))
                    }
                    onBlur={() => { }}
                    error={customerRouteErrors.toWarehouseId}
                    touched={Boolean(customerRouteErrors.toWarehouseId)}
                    isClearable={true}
                    menuPortalTarget={selectMenuPortalTarget}
                  />

                  <div className={customerRouteModalMode === "edit" ? "" : "customer_route_modal_full_width"}>
                    <Input
                      label={requiredLabel("Location Rate")}
                      placeholder="Enter location rate"
                      type="number"
                      minValue="0"
                      name="locationRate"
                      value={customerRouteForm.locationRate}
                      onChange={(event) =>
                        setCustomerRouteForm((previous) => ({
                          ...previous,
                          locationRate: event.target.value,
                        }))
                      }
                      onBlur={() => { }}
                      error={customerRouteErrors.locationRate}
                      touched={Boolean(customerRouteErrors.locationRate)}
                    />
                  </div>

                  {customerRouteModalMode === "edit" && (
                    <div>
                      <Input
                        label={requiredLabel("Rate Changed At")}
                        type="datetime-local"
                        name="changedAt"
                        value={customerRouteForm.changedAt}
                        onChange={(event) => {
                          setCustomerRouteForm((previous) => ({
                            ...previous,
                            changedAt: event.target.value,
                          }));
                          setIsChangedAtDirty(true);
                        }}
                        onBlur={() => { }}
                        error={customerRouteErrors.changedAt}
                        touched={Boolean(customerRouteErrors.changedAt)}
                      />
                    </div>
                  )}

                  <div className="customer_route_modal_full_width">
                    <Input
                      label={requiredLabel("Fixed Distance")}
                      placeholder="Enter fixed distance"
                      type="number"
                      minValue="0"
                      name="fixDistance"
                      value={customerRouteForm.fixDistance}
                      disabled={customerRouteModalMode === "edit"}
                      onChange={(event) =>
                        setCustomerRouteForm((previous) => ({
                          ...previous,
                          fixDistance: event.target.value,
                        }))
                      }
                      onBlur={() => { }}
                      error={customerRouteErrors.fixDistance}
                      touched={Boolean(customerRouteErrors.fixDistance)}
                    />
                  </div>
                </div>

                <div className="customer_route_modal_actions">
                  <button
                    type="button"
                    className="cancel_btn"
                    onClick={closeCustomerRouteModal}
                    disabled={customerRouteSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="main_btn"
                    onClick={handleSubmitCustomerRoute}
                    disabled={routeModalSubmitDisabled}
                  >
                    {customerRouteSubmitting
                      ? customerRouteModalMode === "edit"
                        ? "Updating..."
                        : "Creating..."
                      : customerRouteModalMode === "edit"
                        ? "Update Route"
                        : "Create Route"}
                  </button>
                </div>
              </div>
            </CustomModal>

            <CustomModal
              show={productRateModalOpen}
              handleClose={productRateSubmitting ? () => { } : closeProductRateModal}
              heading="Update Product Rate"
              size="md"
            >
              <div className="customer_route_modal_body">
                <div className="customer_route_modal_fields" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
                  <div>
                    <Input
                      label="Product Name"
                      value={editingProductRate?.name || ""}
                      disabled={true}
                    />
                  </div>

                  <div>
                    <Input
                      label={requiredLabel("Product Value")}
                      placeholder="Enter product value"
                      type="number"
                      minValue="0"
                      name="productValue"
                      value={productRateForm.productValue}
                      onChange={(event) =>
                        setProductRateForm((previous) => ({
                          ...previous,
                          productValue: event.target.value,
                        }))
                      }
                      onBlur={() => { }}
                      error={productRateErrors.productValue}
                      touched={Boolean(productRateErrors.productValue)}
                    />
                  </div>

                  <div>
                    <Input
                      label={requiredLabel("Rate Changed At")}
                      type="date"
                      name="changedAt"
                      value={productRateForm.changedAt}
                      onChange={(event) =>
                        setProductRateForm((previous) => ({
                          ...previous,
                          changedAt: event.target.value,
                        }))
                      }
                      onBlur={() => { }}
                      error={productRateErrors.changedAt}
                      touched={Boolean(productRateErrors.changedAt)}
                    />
                  </div>
                </div>

                <div className="customer_route_modal_actions">
                  <button
                    type="button"
                    className="cancel_btn"
                    onClick={closeProductRateModal}
                    disabled={productRateSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="main_btn"
                    onClick={handleSubmitProductRate}
                    disabled={productRateSubmitting}
                  >
                    {productRateSubmitting ? "Updating..." : "Update Rate"}
                  </button>
                </div>
              </div>
            </CustomModal>

            <ConfirmDeleteModal
              show={locationDeleteModalOpen}
              onClose={closeLocationDeleteModal}
              onConfirm={confirmDeleteLocationTableRow}
              loading={locationSubmitting}
              title="Are you sure you want to delete this location?"
              description={`Deleting "${locationDeleteTarget?.warehouseName || "this location"
                }" will permanently remove it. This action cannot be undone.`}
              confirmText="Delete Location"
            />

            <ConfirmDeleteModal
              show={productDeleteModalOpen}
              onClose={closeProductDeleteModal}
              onConfirm={confirmDeleteProductTableRow}
              loading={productSubmitting}
              title="Are you sure you want to delete this product?"
              description={`Deleting "${productDeleteTarget?.name || "this product"
                }" will permanently remove it. This action cannot be undone.`}
              confirmText="Delete Product"
            />

            <ConfirmDeleteModal
              show={secondaryDeleteModalOpen}
              onClose={closeSecondaryDeleteModal}
              onConfirm={confirmDeleteSecondaryTableRow}
              title="Are you sure you want to delete this contact person?"
              description={`Deleting "${secondaryDeleteTarget?.name || "this contact"
                }" will remove it from this customer. Save customer to persist changes.`}
              confirmText="Delete Contact"
            />

            <CustomModal
              show={customerRouteHistoryModalOpen}
              handleClose={customerRouteHistoryLoading ? () => { } : closeCustomerRouteHistoryModal}
              heading="Route Rate History"
              size="xl"
              hideCloseButton={customerRouteHistoryLoading}
              dialogClassName="customer_route_history_modal_dialog"
            >
              <div className="customer_route_modal_body">
                <div
                  style={{
                    marginBottom: 14,
                    color: "#3D3F5C",
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {viewingCustomerRoute?.fromWarehouseLabel || "--"} to{" "}
                  {viewingCustomerRoute?.toWarehouseLabel || "--"}
                </div>

                <NewTableComponent
                  columns={routeHistoryColumns}
                  data={routeHistoryRows}
                  currentPage={customerRouteHistoryPage}
                  setCurrentPage={setCustomerRouteHistoryPage}
                  limit={customerRouteHistoryLimit}
                  setLimit={setCustomerRouteHistoryLimit}
                  totalItems={customerRouteHistoryTotalItems}
                  totalPages={customerRouteHistoryTotalPages}
                  loading={customerRouteHistoryLoading}
                  paginationLoading={customerRouteHistoryLoading}
                  showAddBtn={false}
                  showFilter={false}
                  tableClassName="customer_routes_table customer_route_history_table"
                  paginationClassName="inline_pagination customer_routes_pagination"
                  noDataComponent={
                    <div className="table_no_records">
                      No rate history found for this route
                    </div>
                  }
                />
              </div>
            </CustomModal>

            <CustomModal
              show={productHistoryModalOpen}
              handleClose={productHistoryLoading || Boolean(productRatesExportLoading) ? () => { } : closeProductHistoryModal}
              heading="Product Rate History"
              size="xl"
              hideCloseButton={productHistoryLoading || Boolean(productRatesExportLoading)}
              dialogClassName="customer_route_history_modal_dialog"
            >
              <div className="customer_route_modal_body">
                <div
                  style={{
                    marginBottom: 14,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      color: "#3D3F5C",
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    Product: {viewingProduct?.name || "--"} ({viewingProduct?.productType || "--"})
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <ExportIconButton
                      type="pdf"
                      onClick={() => handleProductRatesExport("pdf")}
                      loading={productRatesExportLoading === "pdf"}
                      disabled={
                        productHistoryTotalItems === 0 ||
                        productHistoryLoading ||
                        Boolean(productRatesExportLoading)
                      }
                      title={
                        productHistoryTotalItems === 0
                          ? "No rate history to export"
                          : undefined
                      }
                    />
                    <ExportIconButton
                      type="excel"
                      onClick={() => handleProductRatesExport("excel")}
                      loading={productRatesExportLoading === "excel"}
                      disabled={
                        productHistoryTotalItems === 0 ||
                        productHistoryLoading ||
                        Boolean(productRatesExportLoading)
                      }
                      title={
                        productHistoryTotalItems === 0
                          ? "No rate history to export"
                          : undefined
                      }
                    />
                  </div>
                </div>

                <NewTableComponent
                  columns={productHistoryColumns}
                  data={productHistoryRowsMapped}
                  currentPage={productHistoryPage}
                  setCurrentPage={setProductHistoryPage}
                  limit={productHistoryLimit}
                  setLimit={setProductHistoryLimit}
                  totalItems={productHistoryTotalItems}
                  totalPages={productHistoryTotalPages}
                  loading={productHistoryLoading}
                  paginationLoading={productHistoryLoading}
                  showAddBtn={false}
                  showFilter={false}
                  tableClassName="customer_routes_table customer_route_history_table"
                  paginationClassName="inline_pagination customer_routes_pagination"
                  noDataComponent={
                    <div className="table_no_records">
                      No rate history found for this product
                    </div>
                  }
                />
              </div>
            </CustomModal>
          </>
        );
      }}
    </Formik>
  );
};

export default CustomerDetails;
